// ============================================================
// MANIC MEETER — Deutsche Inhalte: Events, Fragen, Minispiele
// ============================================================
// Reiner Text. Keine Logik, keine Balance — die steht in src/events.ts.

import {
  BrainstormingChallenge, BuzzwordBingoChallenge, CodeReviewChallenge,
  EmailSortierenChallenge, KalenderTetrisChallenge, KiHalluzinationChallenge,
  Question, RandomEncounter, SimonSaysChallenge,
} from "../../types";
import type { AddressMethod, AddressPhraseContext, ProcrastinationActivity } from "../contract";

// ---- Prokrastinieren ----

export const PROCRASTINATION_ACTIVITIES: ProcrastinationActivity[] = [
  {
    action: "Du scrollst unauffällig durch dein Handy. Müdigkeit sinkt.",
    caughtAction: "Du wurdest beim Handy-Scrollen ertappt! Peinliche Stille.",
  },
  {
    action: "Du malst kleine Doodles auf deinen Notizblock. Müdigkeit sinkt.",
    caughtAction: "Man hat deine Doodles bemerkt! 'Sehr kreativ...'",
  },
  {
    action: "Du schreibst heimlich eine Einkaufsliste. Müdigkeit sinkt.",
    caughtAction: "Jemand liest deine Einkaufsliste vor! 'Hafermilch, Klopapier...'",
  },
  {
    action: "Du tippst heimlich auf dem Laptop — sieht aus wie Notizen, ist aber Reddit. Müdigkeit sinkt.",
    caughtAction: "Der Teamleiter geht hinter dir vorbei und sieht deinen Reddit-Feed! Ertappt!",
  },
  {
    action: "Du starrst aus dem Fenster und träumst vom Feierabend. Müdigkeit sinkt.",
    caughtAction: "Du wirst aus deinem Tagtraum gerissen! 'Alles klar bei dir?'",
  },
  {
    action: "Du spielst unter dem Tisch mit deinem Schlüsselbund. Müdigkeit sinkt.",
    caughtAction: "Das Klimpern war zu laut. Alle starren dich an.",
  },
  {
    action: "Du zählst die Deckenplatten im Konferenzraum. 47. Müdigkeit sinkt.",
    caughtAction: "'Warum starrst du an die Decke?' — Alle schauen nach oben.",
  },
  {
    action: "Du schreibst eine Nachricht an einen Freund unter dem Tisch. Müdigkeit sinkt.",
    caughtAction: "Dein Handy vibriert lautstark. Alle wissen Bescheid.",
  },
  {
    action: "Du ordnest in Gedanken deine Spotify-Playlist neu. Müdigkeit sinkt.",
    caughtAction: "'Singst du gerade??' — Du hast unbewusst mitgesummt.",
  },
  {
    action: "Du berechnest, wie viele Meetings du in deinem Leben noch überstehen musst. Müdigkeit sinkt.",
    caughtAction: "Dein entsetzter Gesichtsausdruck fällt auf. 'Alles okay?'",
  },
  {
    action: "Du optimierst im Kopf deinen Wocheneinkauf. Müdigkeit sinkt.",
    caughtAction: "'Du murmelst was von Avocados?' — Erwischt.",
  },
  {
    action: "Du tust so, als würdest du Notizen machen, malst aber kleine Katzen. Müdigkeit sinkt.",
    caughtAction: "Der Praktikant sieht deine Katzenzeichnungen. 'Süüüß!' — Alle schauen.",
  },
  {
    action: "Du lässt die KI deine Wortmeldung vorschreiben. Müdigkeit sinkt.",
    caughtAction: "Du liest die KI-Antwort vor. Sie beginnt mit 'Gerne! Hier ist ein möglicher Beitrag:'",
  },
  {
    action: "Du generierst Bilder von Kollegen als mittelalterliche Ritter. Müdigkeit sinkt.",
    caughtAction: "Dein Screen zeigt den Chef als Ritter. Mit sechs Fingern an der Lanze.",
  },
  {
    action: "Du fragst die KI, wie man ein Meeting höflich verlässt. Müdigkeit sinkt.",
    caughtAction: "Jemand liest deinen Prompt mit: 'wie komme ich hier raus ohne dass es auffällt'",
  },
  {
    action: "Du lässt dir das Meeting live zusammenfassen, statt zuzuhören. Müdigkeit sinkt.",
    caughtAction: "'Was war denn dein letzter Punkt?' Deine Zusammenfassung weiß es auch nicht.",
  },
  {
    action: "Du lässt dir vom Assistenten Ausreden für Freitag generieren. Müdigkeit sinkt.",
    caughtAction: "Auf deinem Schirm steht '10 glaubwürdige Gründe, ein Meeting abzusagen'. Punkt 3 ist markiert.",
  },
  {
    action: "Du diskutierst mit einem Chatbot, ob er ein Bewusstsein hat. Müdigkeit sinkt.",
    caughtAction: "'Mit wem tippst du da so angeregt?' — Mit niemandem. Genau genommen.",
  },
  {
    action: "Du lässt dir von der KI dein eigenes Ticket erklären. Müdigkeit sinkt.",
    caughtAction: "Der Teamleiter sieht zu, wie du dir deine eigene Arbeit erklären lässt.",
  },
];

// ---- Fragen im Vorstandsmeeting ----

export const BOSS_QUESTIONS: Question[] = [
  {
    prompt: "Der Chef fragt: 'Warum sind wir beim Umsatz 15% unter Plan?'",
    answers: ["Dazu möchte ich eine Task Force vorschlagen.", "Weil der Plan unrealistisch war.", "Ich dachte, das wäre ein Stretch Goal?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Scrum Master fragt: 'Wie viele Story Points hat das Ticket mit der Farbe Blau?'",
    answers: ["42, wie immer.", "Story Points sind doch nur Richtwerte!", "Das müssen wir im Refinement besprechen."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Product Owner fragt: 'Wann ist das Feature fertig?'",
    answers: ["Wenn es fertig ist.", "Wie definieren wir 'fertig'?", "Gestern."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "HR fragt: 'Wie findest du die Unternehmenskultur?'",
    answers: ["Welche Kultur?", "Ich hab das Kulturhandbuch noch nicht gelesen.", "Die Werte leben wir jeden Tag."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Marketing fragt: 'Wie steigern wir die Conversion Rate?'",
    answers: ["Einfach den Button größer machen.", "A/B-Tests auf der Landing Page.", "Mehr Pop-ups!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Teamleiter fragt: 'Wer hat den Build kaputt gemacht?'",
    answers: ["Git blame sagt: du.", "Das untersuche ich gleich im Post-Mortem.", "Ich war es nicht, ich schwöre!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Chef fragt: 'Was ist unser USP?'",
    answers: ["Unser einzigartiger Tech-Stack und Teamgeist.", "Dass wir trotzdem noch hier sind.", "USB?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Abteilungsleiter fragt: 'Wie steht es um das Quartalsziel?'",
    answers: ["Welches Quartalsziel?", "Wir sind auf Kurs laut aktuellem Tracking.", "Wir haben Quartalsziele?"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Chef fragt: 'Wie können wir innovativer werden?'",
    answers: ["Ein strukturiertes Innovationsprogramm mit Sprints.", "Weniger Meetings, mehr Machen.", "Einfach random Sachen ausprobieren."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Client Manager fragt: 'Der Kunde droht mit Vertragskündigung. Was nun?'",
    answers: ["Ihm drohen, dass wir zuerst kündigen.", "Neuen Kunden suchen.", "Wir setzen ein Eskalationsmeeting auf und liefern einen Action Plan."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Abteilungsleiter fragt: 'Warum ist die Abteilung über Budget?'",
    answers: ["Wir haben die Abweichung identifiziert und steuern gegen.", "Weil alles teurer geworden ist.", "Budget? Wir hatten ein Budget?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "HR fragt: 'Wie können wir die Fluktuation senken?'",
    answers: ["Mehr Gehalt. Fertig.", "Die Tür abschließen.", "Wir analysieren die Exit-Interviews systematisch."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Chef fragt: 'Wo sehen wir uns in 5 Jahren?'",
    answers: ["Als Marktführer im Segment, laut Strategieplan.", "Hoffentlich in einem anderen Meeting.", "Noch hier, wahrscheinlich."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Chef fragt: 'Wie wird die Konkurrenz reagieren?'",
    answers: ["Gar nicht, die wissen nicht wer wir sind.", "Wir beobachten den Markt und reagieren agil.", "Die haben die gleichen Probleme."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Marketing fragt: 'Können wir KI irgendwo einbauen?'",
    answers: ["Klar, überall ein ChatGPT-Logo draufkleben.", "KI macht schon den Kaffee.", "Wir evaluieren Use-Cases mit dem Tech-Team."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der CTO fragt: 'Wie stellen wir sicher, dass unsere KI-Ausgaben korrekt sind?'",
    answers: ["Ein Mensch prüft, bevor etwas rausgeht.", "Wir vertrauen dem Modell.", "Wir lassen eine zweite KI gegenprüfen."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Die CFO fragt: 'Was kostet uns die KI pro Monat?'",
    answers: ["Weiß ich nicht genau.", "Weniger als dieses Meeting.", "Die Aufstellung liegt im Wiki, ich schicke sie gleich."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Vorstand fragt: 'Ersetzt die KI nun Stellen bei uns?'",
    answers: ["Sie verschiebt Aufgaben, nicht Menschen.", "Nur die vom Praktikanten.", "Das entscheidet die KI."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Vorstand fragt: 'Wie messen wir den Nutzen unserer KI-Investition?'",
    answers: ["An eingesparten Stunden, gegengerechnet mit den Lizenzkosten.", "Das lässt sich nicht messen.", "Am Gefühl im Team."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Die CTO fragt: 'Was passiert, wenn der Anbieter morgen die Preise verdoppelt?'",
    answers: ["Dann zahlen wir.", "Wir haben eine Ausstiegsoption evaluiert.", "Darüber habe ich nicht nachgedacht."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
];

// ---- Fragen in normalen Meetings ----

export const MEETING_QUESTIONS: Question[] = [
  {
    prompt: "Jemand fragt: 'Sollen wir das Thema nochmal grundsätzlich diskutieren?'",
    answers: ["Ja, lasst uns alles von vorne aufrollen!", "Nein, wir haben doch schon alles besprochen.", "Ich hätte da noch 5 Punkte..."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Teamleiter fragt: 'Brauchen wir dazu ein Follow-Up-Meeting?'",
    answers: ["Unbedingt, am besten mit 20 Leuten!", "Nein, das können wir per Slack klären.", "Ich würde sogar eine Meeting-Serie vorschlagen."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Hat jemand noch einen Punkt für die Agenda?'",
    answers: ["Ja, ich hätte da was!", "Nein, ich glaube wir sind durch.", "Ich hab sogar eine ganze Präsentation vorbereitet!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Scrum Master fragt: 'Sollen wir die Definition of Done nochmal überarbeiten?'",
    answers: ["Können wir das offline machen?", "Ja! Lasst uns jetzt 45 Minuten darüber reden.", "Ich hab da eine 12-Punkte-Liste..."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wollen wir das Thema vertiefen?'",
    answers: ["Auf jeden Fall, das braucht mindestens eine Stunde.", "Lieber nicht, die Zeit ist knapp.", "Ich hole schnell meine Unterlagen!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Ein Kollege sagt: 'Ich glaube, wir reden aneinander vorbei. Fangen wir nochmal an.'",
    answers: ["Gute Idee!", "Ich glaube, wir waren uns eigentlich einig. Weiter?", "Ja, und diesmal mit Whiteboard!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Soll ich dazu meine Folien zeigen?' fragt der Marketing-Guru.",
    answers: ["Ja, bitte alle 40 Folien!", "Nicht nötig, die Kernpunkte sind klar.", "Kannst du die auch ausdrucken?"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Product Owner fragt: 'Sollen wir alle User Stories nochmal durchgehen?'",
    answers: ["Jede einzelne, mit allen Akzeptanzkriterien!", "Die Top 3 reichen, den Rest klären wir async.", "Ich hätte auch noch 5 neue Stories!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Können wir kurz alle Stakeholder einladen?' fragt der Abteilungsleiter.",
    answers: ["Lieber im kleinen Kreis entscheiden.", "Ja, und am besten auch den Vorstand!", "Ich schicke mal eine Einladung an alle 50."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Jemand sagt: 'Das erinnert mich an ein ähnliches Projekt von 2019...'",
    answers: ["Oh, erzähl!", "Interessant — aber lass uns beim aktuellen Thema bleiben.", "Hast du da eine Case Study zu?"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Sollen wir eine Pro/Contra-Liste an die Tafel schreiben?'",
    answers: ["Nein, abstimmen und weiter.", "Ja! Und danach eine SWOT-Analyse!", "Ich hole die Post-Its!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der HR-Manager fragt: 'Soll ich die Compliance-Richtlinien nochmal vorlesen?'",
    answers: ["Wir kennen sie, danke.", "Ja bitte, von Anfang an!", "Gibt es die auch als Hörbuch?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wollen wir dazu eine Arbeitsgruppe gründen?'",
    answers: ["Super Idee! Mit wöchentlichem Jour Fixe!", "Nein, Entscheidung fällen und umsetzen.", "Ja, und einen Lenkungsausschuss dazu!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Chef fragt: 'Soll ich meine Vision nochmal im Detail erklären?'",
    answers: ["Nein danke, wir haben sie verstanden.", "Ja, am besten mit Anekdoten!", "Können wir ein separates Strategie-Offsite dazu machen?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Sollen wir das Thema in der nächsten Retro nochmal aufgreifen?'",
    answers: ["Nein, ist erledigt.", "Ja, und vorher noch ein Vorbereitungs-Meeting!", "Auf jeden Fall, mit externer Moderation!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Ein Kollege fragt: 'Soll ich den gesamten Kontext erklären?'",
    answers: ["Ja bitte, von Adam und Eva an!", "Kurze Zusammenfassung reicht.", "Kannst du dazu eine Doku schreiben? Für das nächste Meeting?"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wollen wir alle Beteiligten nochmal einzeln fragen?' fragt der Teamleiter.",
    answers: ["Nein, wir haben einen Konsens.", "Ja, jeder hat bestimmt noch was zu sagen!", "Und danach nochmal in Kleingruppen!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Soll ich die Daten nochmal aufbereiten und morgen präsentieren?'",
    answers: ["Ja, und bitte mit drei verschiedenen Diagrammtypen!", "Die Zahlen sind klar genug, wir können entscheiden.", "Mach lieber ein Dashboard daraus!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Praktikant fragt: 'Kann mir jemand erklären, worum es geht?'",
    answers: ["Klar! Also, es fing alles 2017 an...", "Kurz: wir entscheiden X. Details per Chat.", "Ich mache dir eine Zusammenfassung mit 10 Seiten."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Können wir die letzten Meeting-Notizen nochmal durchgehen?'",
    answers: ["Seite für Seite, natürlich!", "Nein, die Action Items stehen fest.", "Ich drucke sie aus, dann lesen wir gemeinsam!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  // ---- 20 neue Fragen mit variierenden richtigen Antworten ----
  {
    prompt: "Der Scrum Master fragt: 'Wie gehen wir mit dem technischen Schulden um?'",
    answers: ["Einfach ignorieren, hat bisher funktioniert.", "Wir planen 20% jedes Sprints für Tech Debt ein.", "Alles neu schreiben! Von Grund auf!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wer übernimmt die Präsentation beim Kunden?'",
    answers: ["Ich mach das — hab mich gut vorbereitet.", "Der Praktikant, zur Übung!", "Können wir die absagen?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Chef fragt: 'Wie stellen wir Qualität sicher?'",
    answers: ["Einfach mehr testen!", "Gar nicht, YOLO.", "Durch automatisierte Tests, Code Reviews und klare Standards."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Was machen wir, wenn der Deploy schiefgeht?'",
    answers: ["Panik!", "Den Rollback-Plan aktivieren, den wir vorab definiert haben.", "Nochmal deployen und hoffen."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Designer fragt: 'Warum sieht das Produkt nicht aus wie im Mockup?'",
    answers: ["Wir arbeiten mit dem Design-Team an der Umsetzung.", "Ist halt so.", "Mockups sind nur Vorschläge."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Sollen wir den Sprint abbrechen?'",
    answers: ["Ja, sofort!", "Auf keinen Fall, wir ziehen das durch!", "Nur wenn das Sprint-Ziel nicht mehr erreichbar ist."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Marketing fragt: 'Wie kommunizieren wir die neue Feature-Verzögerung?'",
    answers: ["Gar nicht. Einfach still sein.", "Transparent und proaktiv mit einem aktualisierten Zeitplan.", "Auf Social Media beschuldigen wir die Technik."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wie verbessern wir unsere Code-Qualität?'",
    answers: ["Pair Programming und regelmäßige Reviews einführen.", "Einfach weniger Code schreiben.", "Mehr Kommentare! Überall Kommentare!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Teamleiter fragt: 'Was war der größte Blocker diese Woche?'",
    answers: ["Die Meetings.", "Unklare Anforderungen — wir brauchen ein Refinement.", "Ich hatte keinen Blocker, alles super!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wollen wir Microservices oder Monolith?'",
    answers: ["Microservices! Immer!", "Monolith! Immer!", "Das kommt auf unsere konkreten Anforderungen und Teamgröße an."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "HR fragt: 'Wie integrieren wir neue Teammitglieder besser?'",
    answers: ["Einfach loslegen lassen, Learning by Doing!", "Ein strukturiertes Onboarding-Programm mit Buddy-System.", "LinkedIn-Profil reicht als Einarbeitung."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Das Feature ist seit 3 Sprints überfällig. Was nun?'",
    answers: ["Scope reduzieren und den Kern liefern.", "Egal, wird schon.", "Mehr Entwickler draufwerfen!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Product Owner fragt: 'Wie priorisieren wir die nächsten Features?'",
    answers: ["Wer am lautesten schreit, kommt zuerst.", "Basierend auf Business Value und Aufwand mit einer Impact-Matrix.", "Eeny, meeny, miny, moe."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Der Kunde will das Feature bis morgen. Was sagen wir?'",
    answers: ["Klar, machen wir!", "Wir kommunizieren einen realistischen Zeitplan mit Begründung.", "Unmöglich! Sag ihm, er soll sich einen anderen Dienstleister suchen!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Backend-Dev fragt: 'Brauchen wir wirklich eine Datenbank-Migration?'",
    answers: ["Ja, das Schema muss für die neuen Features angepasst werden.", "Nö, einfach neue Spalten dranhängen.", "Migration? Einfach neue Datenbank!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wie dokumentieren wir unsere API am besten?'",
    answers: ["Gar nicht, der Code ist selbsterklärend.", "OpenAPI-Spec mit automatischer Generierung.", "In einem Word-Dokument. Per E-Mail verteilt."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Abteilungsleiter fragt: 'Warum braucht das Team ein neues Tool?'",
    answers: ["Weil alle anderen es auch benutzen.", "Das aktuelle Tool verlangsamt unsere Workflows messbar.", "Weil das alte hässlich ist."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Was ist der beste Weg, mit Legacy-Code umzugehen?'",
    answers: ["Alles löschen und neu anfangen.", "Schrittweise refactoren mit Tests als Sicherheitsnetz.", "Nicht anfassen. Nie. Niemals."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Chef fragt: 'Warum haben wir so viele offene Bugs?'",
    answers: ["Bugs? Das sind Features!", "Wir haben ein Bug-Triage eingeführt und priorisieren nach Schweregrad.", "Die QA testet zu gut."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Sollen wir KI in unser Produkt integrieren?'",
    answers: ["Ja! Überall! Sofort! KI macht alles besser!", "Wir evaluieren konkrete Use Cases mit messbarem Nutzen.", "Nein, KI ist nur ein Hype."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Chef fragt: 'Können wir das nicht einfach von der KI machen lassen?'",
    answers: ["Ja, ich generiere das gleich mal.", "Klar, ich schick's dir nachher.", "Ich baue uns bis morgen einen eigenen Agenten dafür!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Hat das jemand gegengelesen, bevor es zum Kunden ging?'",
    answers: ["Die KI hat es geschrieben, also ja.", "Ja, passt alles.", "Ich lasse es sicherheitshalber von einer zweiten KI prüfen."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Scrum Master fragt: 'Wollen wir einen KI-Workshop für alle machen?'",
    answers: ["Ja, am besten zweitägig!", "Eine kurze Seite im Wiki reicht doch.", "Ich könnte den Workshop halten!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wie viel Zeit spart uns die KI eigentlich wirklich?'",
    answers: ["Ungefähr so viel, wie wir mit Prompten verbringen.", "Mindestens 40 Prozent, hab ich gelesen.", "Ich mache dazu eine Erhebung über drei Monate."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Marketing fragt: 'Sollen wir KI-gestützt auf die Website schreiben?'",
    answers: ["Nur wenn es stimmt.", "Ja, das machen doch alle!", "Ich schreibe direkt eine Pressemitteilung dazu."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Wer ist eigentlich verantwortlich, wenn die KI Mist baut?'",
    answers: ["Die KI.", "Wer es rausgeschickt hat.", "Das klären wir, wenn es passiert."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Der Teamleiter fragt: 'Sollen wir unsere Prompts zentral sammeln?'",
    answers: ["Ein gemeinsamer Ordner reicht doch.", "Dafür brauchen wir ein eigenes Tool und ein Gremium.", "Ich baue uns dafür eine Plattform."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Hat die KI-Ausgabe mal jemand mit der Quelle verglichen?'",
    answers: ["Es gab keine Quelle.", "Ja, stimmt überein.", "Dafür hatte ich keine Zeit."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
];

// ---- Brainstorming: Wörter Buchstabe für Buchstabe ----

export const BRAINSTORMING_WORDS: BrainstormingChallenge[] = [
  { word: "Synergieeffekt", timeMs: 10000, context: "Marketing ruft: 'Quick Brainstorm! Tipp das Buzzword ab!'" },
  { word: "Digitaltransformation", timeMs: 12000, context: "Der Chef will Innovation sehen! Tipp schnell!" },
  { word: "Kundenzentrierung", timeMs: 11000, context: "'Customer Centricity' auf Deutsch, bitte!" },
  { word: "Skalierbarkeit", timeMs: 10000, context: "Der Architekt braucht Input — schnell tippen!" },
  { word: "Agilität", timeMs: 7000, context: "Der Scrum Master will Buzzwords hören!" },
  { word: "Innovationskultur", timeMs: 11000, context: "HR möchte das auf die Karriereseite schreiben!" },
  { word: "Wertschöpfungskette", timeMs: 12000, context: "Der Abteilungsleiter präsentiert — hilf ihm!" },
  { word: "Disruption", timeMs: 8000, context: "Jemand hat das Wort in den Raum geworfen!" },
  { word: "Paradigmenwechsel", timeMs: 11000, context: "Der Chef philosophiert — mach mit!" },
  { word: "Prozessoptimierung", timeMs: 12000, context: "Das Lean-Management-Team besteht auf Mitarbeit!" },
  { word: "Stakeholder", timeMs: 9000, context: "Wer sind unsere Stakeholder? Tipp es ab!" },
  { word: "Ressourcenplanung", timeMs: 11000, context: "Der Projektleiter will das Board aktualisieren!" },
  { word: "Compliance", timeMs: 8000, context: "HR fragt nach dem Zauberwort!" },
  { word: "Benchmarking", timeMs: 9000, context: "Marketing braucht Zahlen — und Buzzwords!" },
  { word: "Nachhaltigkeit", timeMs: 10000, context: "ESG-Report muss fertig werden — schnell!" },
  { word: "Microservices", timeMs: 9000, context: "Der Backend-Dev hat eine Architektur-Idee!" },
  { word: "Retrospektive", timeMs: 10000, context: "Was haben wir gelernt? Tipp es!" },
  { word: "Automatisierung", timeMs: 10000, context: "DevOps möchte alles automatisieren!" },
  { word: "Priorisierung", timeMs: 10000, context: "Der PO sortiert das Backlog — hilf ihm!" },
  { word: "Kundenzufriedenheit", timeMs: 12000, context: "Support-Team fragt nach dem KPI!" },
  { word: "Halluzination", timeMs: 11000, context: "Der Chef hat ein neues Lieblingswort. Tipp es ab!" },
  { word: "Kontextfenster", timeMs: 11000, context: "Der Backend-Dev erklärt, warum die KI die Hälfte vergisst!" },
  { word: "Sprachmodell", timeMs: 10000, context: "'Wie heißt das nochmal richtig?' — Tipp es, bevor jemand LLM sagt!" },
  { word: "Promptoptimierung", timeMs: 12000, context: "Runde vier am selben Prompt. Tipp mit!" },
  { word: "Datenqualität", timeMs: 11000, context: "Die unbequemste Antwort auf jede KI-Frage. Tipp sie!" },
];

// ---- Simon Says ----

export const SIMON_SAYS_CHALLENGES: SimonSaysChallenge[] = [
  { sequence: ["↑", "↓", "←", "→", "↑"], timeMs: 7000, context: "Aufstehen, hinsetzen, links, rechts, aufstehen — Team-Building!" },
  { sequence: ["→", "→", "↓", "↑", "←"], timeMs: 7000, context: "Der Scrum Master macht Energizer: 'Alle mitmachen!'" },
  { sequence: ["↑", "↑", "↓", "↓"], timeMs: 6000, context: "Aufstehen, aufstehen, hinsetzen, hinsetzen — Fitness im Büro!" },
  { sequence: ["←", "→", "←", "→", "↑", "↓"], timeMs: 8000, context: "Links-Rechts-Yoga mit dem HR-Manager!" },
  { sequence: ["↑", "←", "↓", "→"], timeMs: 6000, context: "Im Uhrzeigersinn drehen — der Chef macht vor!" },
  { sequence: ["↓", "↓", "↑", "→", "←", "↑"], timeMs: 8000, context: "Der Designer macht eine 'kreative Körperübung'!" },
  { sequence: ["→", "↑", "←", "↓", "→"], timeMs: 7000, context: "Meeting-Warm-up: Der Teamleiter gibt den Takt vor!" },
  { sequence: ["↑", "→", "↓", "←", "↑", "→", "↓"], timeMs: 9000, context: "Agile Fitness: Sprint-Übung im Konferenzraum!" },
  { sequence: ["←", "←", "→", "→", "↑", "↑", "↓"], timeMs: 9000, context: "Der Praktikant hat eine TikTok-Choreo mitgebracht!" },
  { sequence: ["↑", "↓", "↑", "↓", "↑", "↓"], timeMs: 7000, context: "Hoch-Runter-Challenge: Wer kann am schnellsten?" },
  { sequence: ["→", "↑", "→", "↑", "→"], timeMs: 7000, context: "Treppensteigen-Simulation im Sitzen!" },
  { sequence: ["↓", "→", "↑", "←", "↓", "→", "↑", "←"], timeMs: 10000, context: "Vollständige Drehung — zweimal! Achtung, wird schwindelig!" },
  // ---- 20 neue Simon Says Patterns ----
  { sequence: ["↑", "↑", "→", "→", "↓", "↓"], timeMs: 8000, context: "Treppensteigen-Challenge: Hoch, hoch, rechts, rechts, runter, runter!" },
  { sequence: ["←", "↑", "→", "↓"], timeMs: 5000, context: "Der Chef macht die Warm-up-Runde. Kurz und schmerzlos!" },
  { sequence: ["↓", "↑", "↓", "↑", "→", "←", "→"], timeMs: 9000, context: "Der Scrum Master hat ein 'Movement Kata' erfunden!" },
  { sequence: ["→", "→", "→", "←", "←", "←", "↑"], timeMs: 8000, context: "Seitwärts-Shuffle im Konferenzraum! Alle machen mit!" },
  { sequence: ["↑", "←", "↑", "←", "↑", "←"], timeMs: 7000, context: "Diagonal-Sprint zum Whiteboard! Ohne zu stolpern!" },
  { sequence: ["↓", "↓", "↓", "↑", "↑", "↑", "↓", "↑"], timeMs: 10000, context: "Kniebeugen-Marathon: Der Fitness-Guru aus dem HR macht vor!" },
  { sequence: ["→", "↑", "←", "↑", "→", "↑"], timeMs: 7000, context: "Zick-Zack zum Drucker — der Weg ist das Ziel!" },
  { sequence: ["←", "↓", "→", "↑", "←", "↓", "→"], timeMs: 9000, context: "Die Spiral-Challenge: Wer wird als Erster schwindelig?" },
  { sequence: ["↑", "→", "↑", "→", "↑", "→", "↑", "→"], timeMs: 10000, context: "Treppen-Simulation: 4 Stockwerke hoch — aber im Sitzen!" },
  { sequence: ["↓", "←", "↑", "→", "↓"], timeMs: 6000, context: "Quick-Turn: Einmal komplett drehen plus Bonus-Ducken!" },
  { sequence: ["↑", "↑", "↑", "↓"], timeMs: 5000, context: "Dreimal aufstehen und einmal hinsetzen. Kurz und intensiv!" },
  { sequence: ["←", "→", "↑", "↓", "←", "→", "↑", "↓"], timeMs: 10000, context: "Doppelte Kreuz-Challenge! Der Scrum Master dreht auf!" },
  { sequence: ["→", "↓", "←", "↑", "→", "↓"], timeMs: 7000, context: "Uhrzeigersinn-Dreh mit extra Bücken am Ende!" },
  { sequence: ["↑", "←", "↓", "→", "↑", "←", "↓"], timeMs: 9000, context: "Gegen-Uhrzeigersinn-Challenge: Der Designer choreografiert!" },
  { sequence: ["↓", "↓", "→", "→", "↑", "↑", "←", "←"], timeMs: 10000, context: "Das Quadrat tanzen — Abteilungsleiter-Edition!" },
  { sequence: ["↑", "→", "↓", "↓", "←", "↑"], timeMs: 7000, context: "L-Form-Bewegung: Wie ein Schach-Springer!" },
  { sequence: ["←", "←", "↑", "↑", "→", "→", "↓", "↓"], timeMs: 10000, context: "Das Karree: Marketing hat es 'Team-Synergy-Walk' getauft!" },
  { sequence: ["↑", "↓", "→", "←", "↑", "↓", "→", "←", "↑"], timeMs: 11000, context: "Chaos-Sequenz: Der Praktikant hat gewürfelt!" },
  { sequence: ["→", "→", "↑", "←", "←", "↓"], timeMs: 7000, context: "Haus-vom-Nikolaus-Start: Rechts, hoch, links, runter!" },
  { sequence: ["↑", "↑", "→", "↓", "↓", "←", "↑", "→", "↓"], timeMs: 11000, context: "Der ultimative Fitness-Parcours — der Chef schaut zu!" },
  { sequence: ["↑", "↓", "↑", "↓"], timeMs: 6000, context: "Der KI-Avatar des Chefs macht den Energizer vor. Er ruckelt." },
  { sequence: ["→", "→", "←", "←", "↑"], timeMs: 7000, context: "KI-generierte Team-Choreografie. Niemand versteht sie, alle machen mit." },
  { sequence: ["↓", "←", "↑", "→", "↓", "←"], timeMs: 8000, context: "Der Meeting-Bot schlägt eine Bewegungspause vor. Widerspruch zwecklos." },
];

// ---- Buzzword-Bingo ----

export const BUZZWORD_BINGO_CHALLENGES: BuzzwordBingoChallenge[] = [
  {
    words: ["Synergie", "Pivot", "Disruption", "Alignment"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 10000,
    context: "Der Chef ruft Buzzword-Bingo auf! Klicke die Wörter in der Reihenfolge, wie sie gerufen werden!",
  },
  {
    words: ["Skalierung", "Agilität", "Deep Dive", "Low-Hanging-Fruit"],
    correctOrder: [3, 1, 0, 2],
    timeMs: 10000,
    context: "Marketing macht Buzzword-Bingo! Schnell — die richtige Reihenfolge!",
  },
  {
    words: ["Stakeholder", "KPI", "Roadmap", "MVP"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Der PO spielt Buzzword-Bingo mit dem Team! Aufgepasst!",
  },
  {
    words: ["Lean", "Sprint", "Backlog", "Scrum"],
    correctOrder: [3, 2, 0, 1],
    timeMs: 9000,
    context: "Der Scrum Master testet dein Agile-Vokabular!",
  },
  {
    words: ["Onboarding", "Offboarding", "Retention", "Culture Fit"],
    correctOrder: [0, 3, 2, 1],
    timeMs: 10000,
    context: "HR macht ein kleines Quiz — Buzzword-Bingo-Edition!",
  },
  {
    words: ["Cloud", "DevOps", "Microservice", "Container"],
    correctOrder: [2, 3, 0, 1],
    timeMs: 9000,
    context: "Der Backend-Dev will dein Tech-Vokabular testen!",
  },
  {
    words: ["ROI", "Conversion", "Touchpoint", "Branding"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Der Marketing-Guru ruft Buzzwords — triff die Reihenfolge!",
  },
  {
    words: ["Benchmark", "Pipeline", "Iteration", "Velocity"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 10000,
    context: "Team-Meeting: Wer kennt die Buzzwords am besten?",
  },
  // ---- 20 neue Buzzword-Bingo-Challenges ----
  {
    words: ["Enabler", "Leverage", "Silo", "Transparency"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 10000,
    context: "Management-Meeting! Buzzword-Bingo läuft!",
  },
  {
    words: ["Impact", "Ownership", "Empower", "Scale"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Der Chef feuert Buzzwords ab — folge der Reihenfolge!",
  },
  {
    words: ["Ecosystem", "Deliverable", "Mindset", "Framework"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 10000,
    context: "Innovationsmeeting: Buzzword-Alarm!",
  },
  {
    words: ["Bandwidth", "Circle Back", "Drill Down", "Synergy"],
    correctOrder: [0, 3, 1, 2],
    timeMs: 10000,
    context: "Der Client Manager spricht nur in Buzzwords!",
  },
  {
    words: ["Value Stream", "Epic", "Artifact", "Increment"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 9000,
    context: "Agile-Vocabulary-Challenge: Bist du fit?",
  },
  {
    words: ["North Star", "Moonshot", "Game Changer", "Bleeding Edge"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 10000,
    context: "Strategie-Offsite: Buzzword-Regen!",
  },
  {
    words: ["Holistic", "Proactive", "Granular", "Actionable"],
    correctOrder: [3, 1, 0, 2],
    timeMs: 10000,
    context: "HR-Meeting: Wie viele Buzzwords kennst du?",
  },
  {
    words: ["Best Practice", "Pain Point", "Quick Win", "Low Hanging Fruit"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Consulting-Bingo: Alle Klassiker vertreten!",
  },
  {
    words: ["Bootstrapping", "Greenfield", "Brownfield", "Refactoring"],
    correctOrder: [0, 2, 1, 3],
    timeMs: 9000,
    context: "Tech-Meeting: Developer-Buzzword-Edition!",
  },
  {
    words: ["Upskilling", "Cross-Functional", "Talent Pool", "Culture Add"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 10000,
    context: "HR-Offsite: Buzzword-Bingo auf Steroiden!",
  },
  {
    words: ["OKR", "North Star Metric", "Pivot", "Product-Market Fit"],
    correctOrder: [3, 1, 0, 2],
    timeMs: 10000,
    context: "Startup-Buzzword-Challenge: Kennst du sie alle?",
  },
  {
    words: ["GitOps", "IaC", "Observability", "Zero Trust"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 9000,
    context: "DevOps-Meeting: Technische Buzzwords incoming!",
  },
  {
    words: ["Kanban", "Definition of Done", "Blocker", "Impediment"],
    correctOrder: [0, 2, 3, 1],
    timeMs: 9000,
    context: "Daily Standup: Der Scrum Master testet dich!",
  },
  {
    words: ["Blue Ocean", "Red Ocean", "Disruption", "First Mover"],
    correctOrder: [1, 0, 3, 2],
    timeMs: 10000,
    context: "Strategie-Meeting: Buzzwords der Extraklasse!",
  },
  {
    words: ["Scalable", "Modular", "Resilient", "Cloud-Native"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 9000,
    context: "Architektur-Review: Buzzword-Parade!",
  },
  {
    words: ["Omnichannel", "Persona", "Engagement", "Conversion"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Marketing-Standup: Buzzword-Feuerwerk!",
  },
  {
    words: ["Retrospective", "Sprint Goal", "Velocity", "Epic"],
    correctOrder: [2, 3, 0, 1],
    timeMs: 9000,
    context: "Agile-Coach-Challenge: Zeig dein Wissen!",
  },
  {
    words: ["SLA", "Uptime", "Latency", "Throughput"],
    correctOrder: [0, 2, 3, 1],
    timeMs: 9000,
    context: "Operations-Meeting: Performance-Buzzwords!",
  },
  {
    words: ["Growth Hacking", "Viral Loop", "Churn Rate", "CAC"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 10000,
    context: "Growth-Team: Kennst du die Metriken-Buzzwords?",
  },
  {
    words: ["Edge Computing", "Digital Twin", "Metaverse", "Web3"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Innovation Lab: Zukunftsbuzzwords ahoi!",
  },
  {
    words: ["KI-gestützt", "Prompt", "Halluzination", "Agent"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 10000,
    context: "Der Chef erklärt die KI-Strategie. Vier Buzzwords in zwei Minuten — Bingo!",
  },
  {
    words: ["Copilot", "Guardrails", "Use Case", "Human-in-the-Loop"],
    correctOrder: [2, 3, 0, 1],
    timeMs: 10000,
    context: "Die KI-Roadmap wird vorgestellt. Merk dir die Reihenfolge!",
  },
];

// ---- E-Mails sortieren ----

export const EMAIL_SORTIEREN_CHALLENGES: EmailSortierenChallenge[] = [
  {
    emails: [
      { subject: "DRINGEND: Server antwortet nicht mehr!", correct: "wichtig" },
      { subject: "Sie haben 10.000€ gewonnen! Jetzt klicken!", correct: "spam" },
      { subject: "Bitte Kopierpapier im Lager nachfüllen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Dein Postfach quillt über! Sortiere die E-Mails blitzschnell!",
  },
  {
    emails: [
      { subject: "RE: RE: RE: RE: FW: Lustige Katzenbilder", correct: "spam" },
      { subject: "Kunde XYZ droht mit sofortiger Kündigung!", correct: "wichtig" },
      { subject: "Bitte Handtücher im Badezimmer austauschen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "100 ungelesene Mails! Sortiere sie, bevor der Chef kommt!",
  },
  {
    emails: [
      { subject: "Newsletter: '7 Wege zum besseren Manager' (Ausgabe 347)", correct: "spam" },
      { subject: "Sicherheitslücke in Produktion entdeckt!", correct: "wichtig" },
      { subject: "Wer organisiert den Kuchen für Lisas Geburtstag?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Dein Postfach explodiert! Schnell sortieren!",
  },
  {
    emails: [
      { subject: "Gratis-Webinar: 'Blockchain revolutioniert ALLES!'", correct: "spam" },
      { subject: "Board-Präsentation morgen 9 Uhr — dein Part fehlt!", correct: "wichtig" },
      { subject: "Kann jemand die Kaffeemaschine entkalken?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Die Inbox wartet nicht! Sortiere alles richtig!",
  },
  {
    emails: [
      { subject: "Amazon: Ihre Bestellung (Sie haben nichts bestellt)", correct: "spam" },
      { subject: "Deployment FAILED — sofortiger Rollback nötig!", correct: "wichtig" },
      { subject: "Bitte Fotos fürs Intranet-Profil aktualisieren", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Montag morgen, 47 neue Mails. Los geht's!",
  },
  {
    emails: [
      { subject: "Compliance-Audit am Montag — Unterlagen fehlen!", correct: "wichtig" },
      { subject: "Gratis-Pizza!! Klicke HIER für deine Bestellung!", correct: "spam" },
      { subject: "Meetingraum 3.OG für Freitag 14 Uhr buchen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Postfach-Chaos! Wer sortiert am schnellsten?",
  },
  {
    emails: [
      { subject: "WARNUNG: Datenbankkapazität bei 98%!", correct: "wichtig" },
      { subject: "Abnehmen im Schlaf — Ärzte hassen diesen Trick!", correct: "spam" },
      { subject: "Büropflanzen müssen gegossen werden", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox-Alarm! Sortiere, bevor alles überläuft!",
  },
  {
    emails: [
      { subject: "Vorstand möchte sofortige Stellungnahme zum Vorfall!", correct: "wichtig" },
      { subject: "FW: FW: FW: Witzige Büro-Memes 2026 😂😂😂", correct: "spam" },
      { subject: "Druckertoner in Etage 2 wechseln", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Neue Mails! Schnell sortieren, Meeting in 5 Minuten!",
  },
  {
    emails: [
      { subject: "Datenschutzverstoß gemeldet — sofort handeln!", correct: "wichtig" },
      { subject: "Gewinnspiel: iPad Pro GRATIS — nur noch heute!", correct: "spam" },
      { subject: "Wer bestellt neues Whiteboard-Zubehör?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Dein Postfach ruft um Hilfe! Rette es!",
  },
  {
    emails: [
      { subject: "Produktionsausfall Werk 3 — CEO informiert!", correct: "wichtig" },
      { subject: "Krypto-Geheimtipp: 1000% Rendite garantiert!!!", correct: "spam" },
      { subject: "Geburtstagsgeschenk für Herrn Müller organisieren", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "47 Mails und es werden mehr. Los, sortieren!",
  },
  {
    emails: [
      { subject: "Großkunde storniert Vertrag — Eskalation!", correct: "wichtig" },
      { subject: "LinkedIn: 37 Personen haben dein Profil besucht", correct: "spam" },
      { subject: "Parkplatzschilder im Innenhof aktualisieren", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Mails über Mails! Wer blickt da noch durch?",
  },
  {
    emails: [
      { subject: "Firewall-Breach um 03:42 Uhr — Log prüfen!", correct: "wichtig" },
      { subject: "Du wurdest für den 'Best Employee Award' nominiert (Phishing)", correct: "spam" },
      { subject: "Bitte Besucherausweise für Mittwoch vorbereiten", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Montagmorgen-Chaos! Schnell durchsortieren!",
  },
  {
    emails: [
      { subject: "Rechtsabteilung: Frist endet HEUTE 17 Uhr!", correct: "wichtig" },
      { subject: "Exklusives Angebot: Krawatte mit LED-Beleuchtung 🔥", correct: "spam" },
      { subject: "Kühlschrank in der Küche muss gereinigt werden", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Die Inbox ist ein Minenfeld! Vorsichtig sortieren!",
  },
  {
    emails: [
      { subject: "Migration auf neues ERP-System scheitert — Hilfe!", correct: "wichtig" },
      { subject: "RE: RE: Warum Ananas auf Pizza falsch ist (Thread)", correct: "spam" },
      { subject: "Neue Visitenkarten für das Vertriebsteam bestellen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Postfach quillt über — handle schnell!",
  },
  {
    emails: [
      { subject: "Wirtschaftsprüfer kommt morgen unangemeldet!", correct: "wichtig" },
      { subject: "Dein Horoskop für diese Woche: 'Große Veränderungen!'", correct: "spam" },
      { subject: "Blumen für den Empfangsbereich nachbestellen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox-Tsunami! Sortiere dich durch die Flut!",
  },
  {
    emails: [
      { subject: "SSL-Zertifikat läuft in 2 Stunden ab!", correct: "wichtig" },
      { subject: "Umfrage: Welcher Bürohund-Typ bist du? 🐕", correct: "spam" },
      { subject: "Handseife in den Toiletten auffüllen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Schnell sortieren bevor der Bildschirm überläuft!",
  },
  {
    emails: [
      { subject: "Investor-Call in 30 Minuten — Deck unvollständig!", correct: "wichtig" },
      { subject: "Nigerianischer Prinz möchte 5 Mio $ überweisen", correct: "spam" },
      { subject: "Bitte Namensschilder für neue Praktikanten drucken", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Die Inbox brennt! Lösche das Feuer!",
  },
  {
    emails: [
      { subject: "Kunde meldet Datenverlust — Incident eröffnet!", correct: "wichtig" },
      { subject: "FW: Lustige Fail-Videos für die Mittagspause", correct: "spam" },
      { subject: "Wer räumt nach dem Team-Event den Konferenzraum auf?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "73 ungelesene Mails nach dem Urlaub. Durchhalten!",
  },
  {
    emails: [
      { subject: "Betriebsrat verlangt sofortige Stellungnahme!", correct: "wichtig" },
      { subject: "Exklusiv: 90% Rabatt auf Motivations-Poster!", correct: "spam" },
      { subject: "Bitte Firmenwagen zur Inspektion anmelden", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Mail-Lawine! Sortiere dich frei!",
  },
  {
    emails: [
      { subject: "KRITISCH: API-Schlüssel im öffentlichen Repo!", correct: "wichtig" },
      { subject: "Teste JETZT dein biologisches Alter — kostenlos!", correct: "spam" },
      { subject: "Ergonomie-Check für Arbeitsplätze koordinieren", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox-Apokalypse! Rette was zu retten ist!",
  },
  {
    emails: [
      { subject: "Hauptmieter kündigt Bürofläche — Frist 2 Wochen!", correct: "wichtig" },
      { subject: "Newsletter: Die 10 besten Bürostühle 2026", correct: "spam" },
      { subject: "Alte Monitore für Spendenaktion zusammensammeln", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Mail-Marathon! Auf die Plätze, fertig, sortieren!",
  },
  {
    emails: [
      { subject: "Gehaltszahlung fehlgeschlagen — HR kontaktieren!", correct: "wichtig" },
      { subject: "Dein vergessenes Paket wartet! (Tracking: XXX)", correct: "spam" },
      { subject: "Bitte Team-Foto für die Weihnachtskarte organisieren", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Freitagnachmittag, letzte Amtshandlung: Mails sortieren!",
  },
  {
    emails: [
      { subject: "Ransomware-Warnung vom BSI — Systeme prüfen!", correct: "wichtig" },
      { subject: "WhatsApp Gold: Jetzt upgraden für Premium-Features!", correct: "spam" },
      { subject: "Schlüsselkarten für neue Mitarbeiter programmieren", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Dein Postfach hat Fieber! Schnell behandeln!",
  },
  {
    emails: [
      { subject: "Lieferant meldet Insolvenz — Lieferkette gefährdet!", correct: "wichtig" },
      { subject: "EILIG: Dein PayPal-Konto wurde gesperrt (nicht wirklich)", correct: "spam" },
      { subject: "Geschirrspüler in der Teeküche ausräumen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox under pressure! Sortiere unter Zeitdruck!",
  },
  {
    emails: [
      { subject: "Notfall-Patch für Produktivsystem erforderlich!", correct: "wichtig" },
      { subject: "Gratis E-Book: 'Erfolgreich durch Manifestieren'", correct: "spam" },
      { subject: "Wer organisiert Getränke für den Workshop?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Die E-Mail-Flut hört nicht auf! Weitermachen!",
  },
  {
    emails: [
      { subject: "Vertragsentwurf muss bis 12 Uhr unterschrieben sein!", correct: "wichtig" },
      { subject: "Apple Geschenkkarte: Du wurdest ausgewählt! 🎁", correct: "spam" },
      { subject: "Bitte Protokoll vom letzten Teammeeting schreiben", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Noch 20 Mails bis Inbox Zero! Du schaffst das!",
  },
  {
    emails: [
      { subject: "Stromausfall im Rechenzentrum — Notbetrieb!", correct: "wichtig" },
      { subject: "Persönlichkeitstest: Welcher Meeting-Typ bist du?", correct: "spam" },
      { subject: "Fenster in Konferenzraum B klemmt — Hausmeister rufen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Der Posteingang platzt! Rette deine Produktivität!",
  },
  {
    emails: [
      { subject: "Regulierung: Neue DSGVO-Anforderung ab sofort!", correct: "wichtig" },
      { subject: "Verlängere deine Garantie JETZT — letzte Chance!", correct: "spam" },
      { subject: "Kaffeevorrat für die Abteilung nachbestellen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Mail-Dschungel! Finde den Weg durch!",
  },
  {
    emails: [
      { subject: "Sicherheitsvorfall: Unbefugter Zugang zu Serverraum!", correct: "wichtig" },
      { subject: "Dein DNA-Test zeigt: Du bist 2% Wikinger! 🧬", correct: "spam" },
      { subject: "Parkplatzmarkierungen neu streichen lassen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox-Chaos! Nur du kannst Ordnung schaffen!",
  },
  {
    emails: [
      { subject: "Budget-Freeze — alle Ausgaben über 500€ genehmigungspflichtig!", correct: "wichtig" },
      { subject: "RE: FW: Die besten Büro-Pranks (mit Video!)", correct: "spam" },
      { subject: "Bitte Brandschutzunterweisung für Abteilung planen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Die Inbox hat keine Gnade! Sortiere tapfer weiter!",
  },
  {
    emails: [
      { subject: "Ihr KI-Agent hat über Nacht 4.812 Mails beantwortet", correct: "wichtig" },
      { subject: "VERDOPPELN Sie Ihre Produktivität mit AI!!! Jetzt buchen!", correct: "spam" },
      { subject: "Bitte die neue KI-Richtlinie im Wiki verlinken", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Dein KI-Assistent hat vorsortiert. Leider falsch.",
  },
  {
    emails: [
      { subject: "Sicherheitswarnung: API-Key in öffentlichem Repo gefunden", correct: "wichtig" },
      { subject: "Ihr Prompt wurde ausgewählt! Klicken Sie hier für Ihren Preis", correct: "spam" },
      { subject: "Wer pflegt eigentlich unsere Prompt-Sammlung?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Das Postfach nach dem KI-Rollout. Es wurde nicht besser.",
  },
];

// ---- Kalender-Tetris ----

export const KALENDER_TETRIS_CHALLENGES: KalenderTetrisChallenge[] = [
  {
    participants: [
      { name: "Klaus", slots: [true, false, true] },
      { name: "Sabine", slots: [true, false, false] },
      { name: "Jürgen", slots: [false, false, true] },
      { name: "Monika", slots: [true, false, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00"],
    correctSlot: 1,
    timeMs: 10000,
    context: "Meeting muss geplant werden! Wann haben alle Zeit?",
  },
  {
    participants: [
      { name: "Bernd", slots: [false, true, false] },
      { name: "Heike", slots: [true, false, false] },
      { name: "Detlef", slots: [false, true, false] },
      { name: "Gisela", slots: [true, true, false] },
    ],
    timeSlots: ["10:00", "13:00", "15:00"],
    correctSlot: 2,
    timeMs: 10000,
    context: "Dringend ein 1-on-1 einplanen! Welcher Slot ist frei?",
  },
  {
    participants: [
      { name: "Torsten", slots: [true, false, true, false] },
      { name: "Ute", slots: [false, false, false, true] },
      { name: "Herbert", slots: [true, false, false, true] },
      { name: "Petra", slots: [false, false, true, false] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "16:00"],
    correctSlot: 1,
    timeMs: 12000,
    context: "4 Zeitslots, 4 Leute — wann passt es allen?",
  },
  {
    participants: [
      { name: "Rainer", slots: [true, false, true] },
      { name: "Gudrun", slots: [false, false, true] },
      { name: "Achim", slots: [true, false, false] },
      { name: "Bärbel", slots: [true, false, true] },
      { name: "Sven", slots: [false, false, true] },
    ],
    timeSlots: ["09:00", "12:00", "15:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "5 Teilnehmer checken — schnell den freien Slot finden!",
  },
  {
    participants: [
      { name: "Kerstin", slots: [true, false, false, true] },
      { name: "Olaf", slots: [false, false, true, false] },
      { name: "Birgit", slots: [true, false, false, true] },
      { name: "Norbert", slots: [false, false, true, false] },
    ],
    timeSlots: ["08:30", "10:30", "13:30", "15:30"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Der Chef will wissen: Wann können alle?",
  },
  {
    participants: [
      { name: "Elke", slots: [false, true, false] },
      { name: "Hartmut", slots: [false, false, true] },
      { name: "Renate", slots: [false, true, true] },
      { name: "Uwe", slots: [false, true, false] },
      { name: "Margit", slots: [false, true, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Sprint Review eintüten — welcher Slot passt allen?",
  },
  {
    participants: [
      { name: "Dieter", slots: [false, true, true, false] },
      { name: "Ingrid", slots: [true, false, false, false] },
      { name: "Kevin", slots: [false, true, true, false] },
      { name: "Chantal", slots: [true, false, false, false] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 3,
    timeMs: 11000,
    context: "Der Kalender ist ein Minenfeld! Welcher Slot ist frei?",
  },
  {
    participants: [
      { name: "Justin", slots: [true, true, false] },
      { name: "Jessica", slots: [false, true, false] },
      { name: "Malte", slots: [false, true, false] },
      { name: "Svenja", slots: [true, false, false] },
    ],
    timeSlots: ["10:00", "13:00", "16:00"],
    correctSlot: 2,
    timeMs: 10000,
    context: "Retro einschieben — wann sind alle frei?",
  },
  {
    participants: [
      { name: "Bernd", slots: [true, false, false, true] },
      { name: "Sabine", slots: [false, false, true, false] },
      { name: "Klaus", slots: [true, false, false, true] },
      { name: "Heike", slots: [false, false, true, true] },
      { name: "Torsten", slots: [true, false, false, false] },
    ],
    timeSlots: ["08:00", "10:00", "14:00", "16:00"],
    correctSlot: 1,
    timeMs: 12000,
    context: "5 Kollegen, 4 Slots — Chaos im Kalender!",
  },
  {
    participants: [
      { name: "Monika", slots: [true, false, true] },
      { name: "Detlef", slots: [false, false, true] },
      { name: "Gisela", slots: [true, false, false] },
      { name: "Rainer", slots: [true, false, true] },
    ],
    timeSlots: ["09:30", "12:00", "15:00"],
    correctSlot: 1,
    timeMs: 10000,
    context: "Planning-Meeting nötig! Wann ist der Kalender leer?",
  },
  {
    participants: [
      { name: "Gudrun", slots: [false, true, true, false] },
      { name: "Achim", slots: [true, false, true, false] },
      { name: "Bärbel", slots: [false, true, false, false] },
      { name: "Sven", slots: [true, false, true, false] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "15:00"],
    correctSlot: 3,
    timeMs: 11000,
    context: "Wann ist für alle ein Fenster offen?",
  },
  {
    participants: [
      { name: "Kerstin", slots: [true, false, true] },
      { name: "Olaf", slots: [false, false, true] },
      { name: "Norbert", slots: [true, false, false] },
      { name: "Birgit", slots: [false, false, true] },
      { name: "Elke", slots: [true, false, false] },
    ],
    timeSlots: ["09:00", "11:00", "14:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Schnell — der Chef wartet auf den Termin!",
  },
  {
    participants: [
      { name: "Hartmut", slots: [false, true, false, true] },
      { name: "Renate", slots: [false, false, true, false] },
      { name: "Uwe", slots: [false, true, false, true] },
      { name: "Margit", slots: [false, false, true, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Welcher Zeitslot ist für alle frei? Schnell scannen!",
  },
  {
    participants: [
      { name: "Dieter", slots: [false, true, true, false] },
      { name: "Ingrid", slots: [true, false, true, false] },
      { name: "Kevin", slots: [false, true, false, false] },
      { name: "Chantal", slots: [true, true, false, false] },
      { name: "Justin", slots: [false, false, true, false] },
    ],
    timeSlots: ["08:30", "10:30", "13:30", "15:30"],
    correctSlot: 3,
    timeMs: 12000,
    context: "5 Leute, 4 Slots — der Kalender-Albtraum!",
  },
  {
    participants: [
      { name: "Jessica", slots: [true, false, true] },
      { name: "Malte", slots: [true, false, false] },
      { name: "Svenja", slots: [false, false, true] },
      { name: "Klaus", slots: [true, false, true] },
    ],
    timeSlots: ["10:00", "12:00", "15:00"],
    correctSlot: 1,
    timeMs: 10000,
    context: "Brainstorming einplanen — welcher Zeitslot passt?",
  },
  {
    participants: [
      { name: "Sabine", slots: [false, true, false, true] },
      { name: "Bernd", slots: [true, false, false, true] },
      { name: "Heike", slots: [false, true, false, false] },
      { name: "Detlef", slots: [true, false, false, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 2,
    timeMs: 11000,
    context: "Workshop-Termin finden! Wann passt es allen?",
  },
  {
    participants: [
      { name: "Gisela", slots: [true, false, true] },
      { name: "Torsten", slots: [false, false, true] },
      { name: "Ute", slots: [true, false, false] },
      { name: "Herbert", slots: [false, false, true] },
      { name: "Petra", slots: [true, false, false] },
    ],
    timeSlots: ["09:00", "12:00", "15:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Abteilungsmeeting — finde den gemeinsamen Slot!",
  },
  {
    participants: [
      { name: "Rainer", slots: [true, false, true, false] },
      { name: "Gudrun", slots: [false, false, false, true] },
      { name: "Achim", slots: [false, false, true, false] },
      { name: "Bärbel", slots: [true, false, true, true] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "16:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Eskalationsmeeting! Wann sind alle verfügbar?",
  },
  {
    participants: [
      { name: "Sven", slots: [false, true, false] },
      { name: "Kerstin", slots: [true, true, false] },
      { name: "Olaf", slots: [false, false, false] },
      { name: "Birgit", slots: [true, true, false] },
      { name: "Norbert", slots: [false, true, false] },
    ],
    timeSlots: ["10:00", "13:00", "16:00"],
    correctSlot: 2,
    timeMs: 11000,
    context: "Kundenmeeting planen — welcher Slot geht für alle?",
  },
  {
    participants: [
      { name: "Elke", slots: [false, true, false, true] },
      { name: "Hartmut", slots: [false, false, true, false] },
      { name: "Renate", slots: [false, true, false, true] },
      { name: "Uwe", slots: [false, true, true, false] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Vorstandspräsentation — der Kalender ist überfüllt!",
  },
  {
    participants: [
      { name: "Margit", slots: [true, true, false, true] },
      { name: "Dieter", slots: [false, true, false, false] },
      { name: "Ingrid", slots: [false, false, false, true] },
      { name: "Kevin", slots: [true, false, false, false] },
      { name: "Chantal", slots: [false, true, false, true] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "15:00"],
    correctSlot: 2,
    timeMs: 12000,
    context: "5 Teilnehmer, 4 Zeitslots — maximales Chaos!",
  },
  {
    participants: [
      { name: "Justin", slots: [true, false, true] },
      { name: "Jessica", slots: [false, false, true] },
      { name: "Malte", slots: [true, false, false] },
      { name: "Svenja", slots: [false, false, true] },
    ],
    timeSlots: ["09:30", "12:30", "15:30"],
    correctSlot: 1,
    timeMs: 10000,
    context: "Jour Fixe einschieben — wann ist alles frei?",
  },
  {
    participants: [
      { name: "Klaus", slots: [true, false, true, false] },
      { name: "Sabine", slots: [false, false, false, true] },
      { name: "Bernd", slots: [true, false, false, false] },
      { name: "Monika", slots: [false, false, true, true] },
    ],
    timeSlots: ["08:30", "11:00", "13:30", "16:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Refinement-Termin nötig! Welcher Slot passt allen?",
  },
  {
    participants: [
      { name: "Heike", slots: [false, true, true] },
      { name: "Detlef", slots: [false, false, true] },
      { name: "Gisela", slots: [false, true, true] },
      { name: "Torsten", slots: [false, true, false] },
      { name: "Ute", slots: [false, true, true] },
    ],
    timeSlots: ["10:00", "13:00", "15:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Demo-Termin — wann können alle fünf?",
  },
  {
    participants: [
      { name: "Herbert", slots: [false, true, false, true] },
      { name: "Petra", slots: [false, false, true, true] },
      { name: "Rainer", slots: [false, true, false, false] },
      { name: "Gudrun", slots: [false, false, true, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Ganztags-Workshop — wann passt es wirklich allen?",
  },
  {
    participants: [
      { name: "Achim", slots: [true, false, true] },
      { name: "Bärbel", slots: [false, false, true] },
      { name: "Sven", slots: [true, false, false] },
      { name: "Kerstin", slots: [true, false, true] },
      { name: "Olaf", slots: [false, false, true] },
    ],
    timeSlots: ["08:00", "12:00", "16:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Der Scrum Master braucht JETZT einen Termin!",
  },
  {
    participants: [
      { name: "Birgit", slots: [false, true, true, false] },
      { name: "Norbert", slots: [true, false, true, false] },
      { name: "Elke", slots: [false, true, false, false] },
      { name: "Hartmut", slots: [true, false, true, false] },
    ],
    timeSlots: ["09:00", "10:30", "13:00", "15:30"],
    correctSlot: 3,
    timeMs: 11000,
    context: "Compliance-Meeting — welcher Slot ist bei allen frei?",
  },
  {
    participants: [
      { name: "Renate", slots: [true, false, true] },
      { name: "Uwe", slots: [false, false, true] },
      { name: "Margit", slots: [true, false, false] },
      { name: "Dieter", slots: [false, false, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00"],
    correctSlot: 1,
    timeMs: 10000,
    context: "Quick-Sync einplanen — wann passt's allen?",
  },
  {
    participants: [
      { name: "Ingrid", slots: [true, false, false, true] },
      { name: "Kevin", slots: [false, false, true, false] },
      { name: "Chantal", slots: [true, false, false, true] },
      { name: "Justin", slots: [false, false, true, false] },
      { name: "Jessica", slots: [true, false, false, true] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "16:00"],
    correctSlot: 1,
    timeMs: 12000,
    context: "Town Hall organisieren — welcher Slot ist konfliktfrei?",
  },
  {
    participants: [
      { name: "Malte", slots: [false, true, true, false] },
      { name: "Svenja", slots: [true, false, true, false] },
      { name: "Klaus", slots: [false, true, false, false] },
      { name: "Sabine", slots: [true, false, true, false] },
    ],
    timeSlots: ["09:00", "11:30", "14:00", "16:30"],
    correctSlot: 3,
    timeMs: 11000,
    context: "Strategie-Workshop — wann ist bei allen alles frei?",
  },
  {
    participants: [
      { name: "Marcel", slots: [true, true, false, true] },
      { name: "Nadja", slots: [false, true, false, true] },
      { name: "Ingo", slots: [true, false, false, true] },
    ],
    timeSlots: ["09:00", "11:00", "13:00", "16:00"],
    correctSlot: 2,
    timeMs: 10000,
    context: "Der KI-Assistent hat drei Termine vorgeschlagen. Alle kollidieren.",
  },
];

// ---- Code Review ----

export const CODE_REVIEW_CHALLENGES: CodeReviewChallenge[] = [
  {
    code: "if (user.role = 'admin') {\n  grantAccess();\n}",
    options: ["== statt =", "=== statt =", "!= statt ="],
    correctIndex: 1,
    language: "JavaScript",
    timeMs: 10000,
    context: "Code Review! Finde den Bug im Auth-Code!",
  },
  {
    code: "for (let i = 0; i <= arr.length; i++) {\n  process(arr[i]);\n}",
    options: ["i < arr.length", "i >= arr.length", "i != arr.length"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Off-by-one Error? Der Klassiker!",
  },
  {
    code: "const data = await fetch('/api');\nconst json = data.json();",
    options: ["await data.json()", "data.text()", "JSON.parse(data)"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Async/Await Bug — findest du ihn?",
  },
  {
    code: "SELECT * FROM users\nWHERE name = 'admin'\nOR 1=1; --'",
    options: ["Prepared Statements nutzen", "Einfach OR entfernen", "WHERE weglassen"],
    correctIndex: 0,
    language: "SQL",
    timeMs: 10000,
    context: "SQL Injection! Wie fixst du das?",
  },
  {
    code: "const timer = setInterval(() => {\n  update();\n}, 1000);\n// Komponente wird zerstört",
    options: ["clearInterval(timer) beim Cleanup", "setTimeout statt setInterval", "update() direkt aufrufen"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Memory Leak! Der Timer läuft ewig!",
  },
  {
    code: "password = md5(userInput);",
    options: ["SHA-256 verwenden", "bcrypt/argon2 mit Salt verwenden", "Base64 statt md5"],
    correctIndex: 1,
    language: "Python",
    timeMs: 10000,
    context: "Security-Review! Das Passwort-Hashing ist unsicher!",
  },
  {
    code: "console.log(0.1 + 0.2 == 0.3);\n// Ergebnis: false",
    options: ["=== verwenden", "Math.abs(a-b) < epsilon prüfen", "parseInt verwenden"],
    correctIndex: 1,
    language: "JavaScript",
    timeMs: 10000,
    context: "Floating-Point-Bug! Wie vergleicht man korrekt?",
  },
  {
    code: "try {\n  riskyOperation();\n} catch (e) {\n  // TODO: handle error\n}",
    options: ["throw e; hinzufügen", "Error loggen und sinnvoll behandeln", "try-catch entfernen"],
    correctIndex: 1,
    language: "TypeScript",
    timeMs: 10000,
    context: "Leerer Catch-Block! Fehler werden verschluckt!",
  },
  {
    code: "const apiKey = 'sk-1234abcd';\nfetch('/api', { headers: { key: apiKey }});",
    options: ["API-Key in Umgebungsvariable auslagern", "API-Key in Base64 encoden", "API-Key in Kommentar schreiben"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Hardcoded Secret! Der Security-Scanner schlägt Alarm!",
  },
  {
    code: "innerHTML = '<div>' + userInput + '</div>';",
    options: ["textContent statt innerHTML", "userInput in Anführungszeichen", "encodeURI(userInput)"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "XSS-Lücke! User-Input wird direkt gerendert!",
  },
  {
    code: "if (arr.length > 0)\n  doFirst();\n  doSecond();",
    options: ["Geschweifte Klammern um den Block", "Semikolon nach doFirst()", "else hinzufügen"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Dangling-Statement! doSecond() läuft immer!",
  },
  {
    code: "const result = numbers.sort();\n// [1, 10, 2, 20, 3]",
    options: ["sort((a,b) => a - b)", "sort().reverse()", "sort(Number)"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Sort-Bug! Zahlen werden als Strings sortiert!",
  },
  {
    code: "useEffect(() => {\n  setCount(count + 1);\n});",
    options: ["Dependency-Array [] hinzufügen", "useCallback verwenden", "count als State entfernen"],
    correctIndex: 0,
    language: "React",
    timeMs: 10000,
    context: "Infinite Render Loop! Die Komponente crasht!",
  },
  {
    code: "const config = JSON.parse(fs.readFileSync('config.json'));\n// Im Request-Handler",
    options: ["Datei einmal beim Start laden und cachen", "JSON.stringify verwenden", "YAML statt JSON"],
    correctIndex: 0,
    language: "Node.js",
    timeMs: 10000,
    context: "Performance-Bug! Datei wird bei jedem Request gelesen!",
  },
  {
    code: "DELETE FROM orders\nWHERE status = 'cancelled'",
    options: ["LIMIT hinzufügen oder Backup vorher", "Transaktion mit ROLLBACK-Option", "UPDATE statt DELETE"],
    correctIndex: 1,
    language: "SQL",
    timeMs: 10000,
    context: "Gefährliches DELETE ohne Sicherheitsnetz!",
  },
  {
    code: "git push --force origin main",
    options: ["--force-with-lease verwenden", "--force-all verwenden", "git reset vorher"],
    correctIndex: 0,
    language: "Git",
    timeMs: 10000,
    context: "Force Push auf main! Das ist gefährlich!",
  },
  {
    code: "while (true) {\n  const data = fetchData();\n  if (data) break;\n}",
    options: ["Sleep/Delay zwischen Versuchen", "Timeout entfernen", "fetchData() cachen"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Busy Wait! CPU dreht am Rad!",
  },
  {
    code: "const users = db.query(\n  `SELECT * FROM users WHERE id = ${userId}`\n);",
    options: ["Template-String mit Escaping", "Parameterized Query verwenden", "userId in parseInt wrappen"],
    correctIndex: 1,
    language: "Node.js",
    timeMs: 10000,
    context: "SQL Injection durch String-Interpolation!",
  },
  {
    code: "function getUser(id) {\n  return db.find(id);\n}\n// Returns undefined or User",
    options: ["Immer null zurückgeben", "Optional mit ? verketten", "Return-Type explizit deklarieren & Null-Check"],
    correctIndex: 2,
    language: "TypeScript",
    timeMs: 10000,
    context: "Type Safety! Die Funktion kann undefined zurückgeben!",
  },
  {
    code: "localStorage.setItem('token', jwt);\n// Auf jeder Seite verfügbar",
    options: ["SessionStorage verwenden", "HttpOnly Cookie statt localStorage", "Token im URL-Parameter"],
    correctIndex: 1,
    language: "JavaScript",
    timeMs: 10000,
    context: "JWT im localStorage — XSS-Gefahr!",
  },
  {
    code: "if (user && user.address && user.address.city)\n  return user.address.city;",
    options: ["Optional Chaining: user?.address?.city", "try-catch drumherum", "user.address!.city"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Null-Check-Kette! Es gibt was Besseres!",
  },
  {
    code: "import moment from 'moment';\n// Bundle: 67KB gzipped",
    options: ["Eigene Datumsfunktionen schreiben", "date-fns oder dayjs verwenden", "Moment.js ist perfekt so"],
    correctIndex: 1,
    language: "JavaScript",
    timeMs: 10000,
    context: "Bundle-Size! Moment.js ist riesig!",
  },
  {
    code: "res.send({ error: err.stack });",
    options: ["Generische Fehlermeldung senden", "err.message statt stack", "Error in console.log schreiben"],
    correctIndex: 0,
    language: "Express",
    timeMs: 10000,
    context: "Stack Trace an den Client! Information Disclosure!",
  },
  {
    code: "const arr = [1, 2, 3];\ndelete arr[1];\n// [1, empty, 3]",
    options: ["arr.splice(1, 1) verwenden", "arr.filter statt delete", "arr.splice(1, 1) oder arr.filter"],
    correctIndex: 2,
    language: "JavaScript",
    timeMs: 10000,
    context: "Array-Loch! delete erzeugt empty slots!",
  },
  {
    code: "// .env\nDB_PASSWORD=admin123",
    options: ["Stärkeres Passwort verwenden", "Secrets Manager/Vault verwenden", "Passwort verschlüsseln"],
    correctIndex: 1,
    language: "Config",
    timeMs: 10000,
    context: "Passwort in der .env! Und es heißt admin123!",
  },
  {
    code: "app.use(cors({ origin: '*' }));",
    options: ["Spezifische Origins whitelisten", "CORS komplett entfernen", "credentials: true hinzufügen"],
    correctIndex: 0,
    language: "Express",
    timeMs: 10000,
    context: "CORS wildcard! Jede Domain hat Zugriff!",
  },
  {
    code: "new Date().getYear()\n// Erwartet: 2026, Ergebnis: 126",
    options: ["getFullYear() verwenden", "getYear() + 2000", "Date.now().year"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Date-Bug! getYear() gibt Jahre seit 1900 zurück!",
  },
  {
    code: "setTimeout(doSomething(), 1000);",
    options: ["doSomething ohne Klammern übergeben", "Callback mit clearTimeout", "setInterval statt setTimeout"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Funktion wird sofort ausgeführt statt nach 1s!",
  },
  {
    code: "const copy = originalArray;\ncopy.push('new');\n// originalArray ist auch geändert!",
    options: ["[...originalArray] oder Array.from()", "copy = originalArray.slice()", "Beides möglich — Spread oder Slice"],
    correctIndex: 2,
    language: "JavaScript",
    timeMs: 10000,
    context: "Shallow Copy vergessen! Referenz statt Kopie!",
  },
  {
    code: "if (status === 200 || 201) {\n  success();\n}",
    options: ["status === 200 || status === 201", "[200, 201].includes(status)", "Beides möglich"],
    correctIndex: 2,
    language: "JavaScript",
    timeMs: 10000,
    context: "Logischer Fehler! 201 ist immer truthy!",
  },
  {
    code: "async function save() {\n  db.insert(data);\n  return 'saved';\n}",
    options: ["await db.insert(data)", "return db.insert(data)", "try-catch hinzufügen"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Fehlendes await! Die DB-Operation wird nicht abgewartet!",
  },
  {
    code: "const el = document.getElementById('btn');\nel.addEventListener('click', handler);\n// el könnte null sein",
    options: ["Null-Check vor addEventListener", "el! (Non-null assertion)", "querySelector statt getElementById"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Null-Zugriff! getElementById kann null zurückgeben!",
  },
  {
    code: "for (var i = 0; i < 5; i++) {\n  setTimeout(() => log(i), 100);\n}\n// Gibt 5x '5' aus",
    options: ["let statt var verwenden", "const statt var verwenden", "i als Parameter übergeben"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Closure-Bug! var hat keinen Block-Scope!",
  },
  {
    code: "typeof null\n// Ergebnis: 'object'",
    options: ["value === null prüfen", "typeof value !== 'object'", "isNull(value) verwenden"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "typeof null ist 'object'! Historischer JS-Bug!",
  },
  {
    code: "git commit -m 'fix'\ngit commit -m 'fix2'\ngit commit -m 'fix fix'",
    options: ["Aussagekräftige Commit-Messages schreiben", "git squash verwenden", "Beides — Squash und bessere Messages"],
    correctIndex: 2,
    language: "Git",
    timeMs: 10000,
    context: "Commit-History-Chaos! Was wurde hier geändert?",
  },
  {
    code: "const response = fetch('/api');\nif (response.ok) { ... }",
    options: ["await vor fetch()", "response.status prüfen", ".then() verwenden"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Promise nicht aufgelöst! fetch ist async!",
  },
  {
    code: "// von der KI generiert\nconst total = items.reduce((a, b) => a + b);",
    options: ["Startwert 0 ergänzen", "map statt reduce", "Semikolon entfernen"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Die KI hat den Reducer geschrieben. Bei leerer Liste kracht es.",
  },
  {
    code: "const key = 'sk-prod-4f8a...';\nawait ai.complete(prompt, { key });",
    options: ["Key aus der Umgebung laden", "Key umbenennen", "Key in einen Kommentar schreiben"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Der Assistent hat den API-Key gleich mit eingefügt. Sehr hilfsbereit.",
  },
];

// ---- KI-HALLUZINATIONEN ----
/**
 * The assistant wrote it, somebody has to read it.
 *
 * Every slide here is mostly true, which is the whole problem: the invented
 * lines are the ones that sound best. A run of these teaches exactly one
 * lesson, and it is the right one — the more flattering a claim is, the more
 * likely nobody checked it.
 */
export const KI_HALLUZINATION_CHALLENGES: KiHalluzinationChallenge[] = [
  {
    tool: "MeetGPT-5",
    task: "Fasse den Sprint zusammen. Positiv.",
    statements: [
      "Das Team hat 47 von 52 Story Points abgeschlossen.",
      "Die Velocity ist gegenüber dem Vorquartal um 12% gestiegen.",
      "Kevin hat die Datenbank-Migration eigenständig über Nacht abgeschlossen.",
      "Zwei Tickets wurden in den nächsten Sprint verschoben.",
      "Das Team wurde für den Deutschen Innovationspreis 2026 nominiert.",
    ],
    hallucinations: [2, 4],
    timeMs: 15000,
    context: "Die KI hat den Sprint-Report geschrieben. Zwei Sätze davon sind erfunden.",
  },
  {
    tool: "Meeting-Copilot",
    task: "Schreib das Protokoll vom Daily.",
    statements: [
      "Sandra berichtet von Fortschritten im Backend.",
      "Es wurden keine Blocker gemeldet.",
      "Du hast zugesagt, die Migration bis Freitag allein zu erledigen.",
      "Das nächste Daily findet wie gewohnt um 9:15 Uhr statt.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "Das KI-Protokoll geht gleich an alle. Lies es, bevor es der Chef tut.",
  },
  {
    tool: "InsightAI Pro",
    task: "Analysiere das Kundenfeedback aus dem letzten Quartal.",
    statements: [
      "Die Zufriedenheit liegt bei 3,8 von 5 Punkten.",
      "Am häufigsten kritisiert wurde die Ladezeit.",
      "Die Müller GmbH hat ihren Vertrag um fünf Jahre verlängert.",
      "14% der Befragten haben gar nicht geantwortet.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "Die Auswertung ist fertig. Ob sie stimmt, hat niemand gefragt.",
  },
  {
    tool: "DeepMarket 3",
    task: "Erstell eine Wettbewerbsanalyse. Eine Seite.",
    statements: [
      "Der Markt wächst laut Studie jährlich um 4%.",
      "Unser Hauptwettbewerber hat dieses Jahr Insolvenz angemeldet.",
      "Drei neue Anbieter sind in den Markt eingetreten.",
      "Die zugrunde liegende Studie stammt aus dem Jahr 2019.",
      "Wir sind laut Analystenrating Marktführer in unserem Segment.",
    ],
    hallucinations: [1, 4],
    timeMs: 15000,
    context: "Marktanalyse in 4 Sekunden generiert. Schön wär's.",
  },
  {
    tool: "Copilot Enterprise",
    task: "Dokumentiere die Auth-Klasse.",
    statements: [
      "Die Klasse validiert Tokens gegen den Auth-Server.",
      "Sie nutzt intern die Bibliothek authlib-turbo.",
      "Abgelaufene Tokens werfen einen AuthError.",
      "Die Methode refresh() ist seit Version 2.1 deprecated.",
    ],
    hallucinations: [1, 3],
    timeMs: 14000,
    context: "Die Doku ist generiert. Die Hälfte der genannten Pakete existiert.",
  },
  {
    tool: "Talentia HR",
    task: "Fasse den Lebenslauf des Bewerbers zusammen.",
    statements: [
      "Acht Jahre Berufserfahrung im Backend.",
      "Spricht fließend Deutsch und Englisch.",
      "Hat 2021 ein eigenes Startup an einen US-Konzern verkauft.",
      "Wechselt im Schnitt alle 18 Monate die Stelle.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "Der KI-Screening-Report. Der Bewerber sitzt schon draußen.",
  },
  {
    tool: "FinBot",
    task: "Bereite die Quartalszahlen fürs Management auf.",
    statements: [
      "Der Umsatz liegt 3% unter Plan.",
      "Die Personalkosten sind gestiegen.",
      "Das Unternehmen ist seit dem zweiten Quartal schuldenfrei.",
      "Für Q4 wird eine Seitwärtsbewegung erwartet.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "Zahlen von der KI. Der CFO liest gleich mit.",
  },
  {
    tool: "StrategyGPT",
    task: "Mach eine Roadmap. Muss ambitioniert aussehen.",
    statements: [
      "Feature A ist für Q1 geplant.",
      "Feature B braucht noch eine Abstimmung mit dem Datenschutz.",
      "Die KI-Integration ist bereits vollständig implementiert.",
      "Der Launch-Termin steht noch nicht fest.",
      "Der Vorstand hat die Roadmap einstimmig freigegeben.",
    ],
    hallucinations: [2, 4],
    timeMs: 15000,
    context: "Die Roadmap kommt aus dem Prompt. Die Zusagen leider auch.",
  },
  {
    tool: "LearnBot",
    task: "Fasse die Pflichtschulung zusammen.",
    statements: [
      "Die Schulung dauert zwei Stunden.",
      "Alle Mitarbeitenden haben sie bereits absolviert.",
      "Am Ende steht ein Quiz mit zehn Fragen.",
      "Die Teilnahme ist verpflichtend.",
    ],
    hallucinations: [1],
    timeMs: 13000,
    context: "Compliance-Zusammenfassung, KI-generiert. Was soll schon schiefgehen.",
  },
  {
    tool: "PipelineGPT",
    task: "Bewerte unsere Pipeline für den Vorstand.",
    statements: [
      "Im Trichter liegen 34 Deals.",
      "Die durchschnittliche Abschlussquote liegt bei 18%.",
      "Drei Kunden haben mündlich bereits zugesagt.",
      "Zwei Deals sind seit acht Monaten unverändert.",
      "Der Vertrieb hat die Prognose bestätigt.",
    ],
    hallucinations: [2, 4],
    timeMs: 15000,
    context: "Die KI hat die Pipeline bewertet. Optimistisch ist gar kein Ausdruck.",
  },
  {
    tool: "OpsCopilot",
    task: "Schreib das Post-Mortem zum Ausfall.",
    statements: [
      "Der Ausfall dauerte 42 Minuten.",
      "Ursache war ein fehlerhaftes Deployment.",
      "Kein Kunde hat den Ausfall bemerkt.",
      "Das Rollback lief vollautomatisch.",
      "Ein Follow-up-Ticket wurde angelegt.",
    ],
    hallucinations: [2, 3],
    timeMs: 15000,
    context: "Das Post-Mortem ist generiert. Es liest sich erstaunlich entspannt.",
  },
  {
    tool: "DocuBot",
    task: "Schreib die Onboarding-Doku für neue Devs.",
    statements: [
      "Das Setup dauert etwa einen halben Tag.",
      "Die Datenbank läuft lokal in Docker.",
      "Ein Aufruf von setup.sh richtet alles ein.",
      "Fragen beantwortet das Team im Chat.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "Die Onboarding-Doku ist fertig. Eines der Skripte darin existiert nicht.",
  },
  {
    tool: "ESG-Assistent",
    task: "Formuliere unseren Nachhaltigkeitsbericht.",
    statements: [
      "Der Stromverbrauch ist um 6% gesunken.",
      "Wir kompensieren seit diesem Jahr alle Dienstreisen.",
      "Die Zahlen stammen aus dem internen Reporting.",
      "Ein externes Audit steht noch aus.",
      "Wir sind bereits klimaneutral.",
    ],
    hallucinations: [1, 4],
    timeMs: 15000,
    context: "Der ESG-Bericht kommt aus dem Prompt. Die Prüfer kommen später.",
  },
];

// ---- Flavor-Einwürfe während des Meetings ----

export const RANDOM_MEETING_FLAVOR = [
  // Klassiker
  "Jemand hat die Kamera an mit Katzenhintergrund.",
  "Jemandes Mikro war nicht auf Mute. Man hört einen Staubsauger.",
  "Der Beamer funktioniert nicht. Überraschung.",
  "Jemand tippt hörbar und aggressiv auf seiner Tastatur.",
  "Eine Feuerwehrsirene ertönt draußen. Niemand reagiert.",
  "'Hört ihr mich? HÖRT IHR MICH??' — Ja, tun wir.",
  "Ein Kollege isst Chips. Laut.",
  "'Sorry, ich war auf Mute' — Zum 5. Mal heute.",
  "Der Praktikant teilt versehentlich seinen Screen. Es ist Reddit.",
  "'Wir müssen das offline besprechen.' — Was heißt das überhaupt?",
  "Jemand hat den Outlook-Kalender geteilt. 12 Meetings heute.",
  "'Können wir das kurz halten?' — Narrator: Sie konnten nicht.",
  "PowerPoint-Präsentation: Folie 1 von 87.",
  "Der Chef erzählt einen Witz. Höfliches Lachen.",
  "Ein Handy klingelt. Es ist der Macarena-Remix.",
  "Jemand hat die falsche Einwahlnummer benutzt. Dreimal.",
  "Der Kaffee ist alle. Panik bricht aus.",
  "Jemand isst einen Apfel. Microphon-Gain auf Maximum.",
  "Ein Kollege nimmt aus dem Auto teil. Man hört den Blinker.",
  "Die Klimaanlage springt an. Arktische Temperaturen.",
  "Jemand hat 47 Folien vorbereitet. Für ein 15-Minuten-Meeting.",
  "Der Bildschirm flackert. IT wurde informiert. Vor 3 Wochen.",
  "'Das ist ein guter Punkt.' — Niemand weiß, welcher Punkt gemeint ist.",
  "Jemand hat sich in den Konferenzraum eingesperrt.",
  "Der Kaffeeautomat macht bedrohliche Geräusche.",
  "'Können wir das parken?' — Das Meeting-Parkplatz ist voll.",
  "Jemand hat den falschen Raum gebucht. Doppelbelegung.",
  "Der Drucker druckt plötzlich 200 Seiten. Niemand weiß woher.",
  "Ein Fenster steht offen. Straßenlärm dominiert.",
  "Jemand hat Kuchen mitgebracht. Die Stimmung hebt sich kurz.",
  "Der Laptop eines Kollegen geht in den Standby. Mitten im Satz.",
  "'Wer schreibt Protokoll?' — Stille. Blicke auf den Praktikanten.",
  "Der WLAN-Empfang bricht zusammen. Kurze Erlösung.",
  "Jemand nutzt ein Whiteboard. Die Stifte sind alle trocken.",
  "Ein Kollege telefoniert leise in der Ecke. Nicht leise genug.",
  "'Ich muss in 5 Minuten zum nächsten Call.' — Sagt jeder.",
  "Ein Baby weint im Hintergrund eines Home-Office-Teilnehmers.",
  "Jemand hat sein Frühstück mitgebracht. Es riecht nach Thunfisch.",
  "Die Putzfrau kommt rein und saugt um den Tisch herum.",
  "Jemand sagt 'Synergie'. Drei Leute stöhnen gleichzeitig.",
  "Der Beamer zeigt nur ein blaues Bild. 'Moment, ich fix das...'",
  "'Ich hab noch einen Hard Stop um 14 Uhr.' — Es ist 13:58.",
  "Ein Kollege spielt heimlich mit seinem Kugelschreiber. Klick. Klick. Klick.",
  "Jemand hat vergessen, dass das Meeting heute ist. Peinlicher Späteintritt.",
  "Der Teamleiter liest eine E-Mail vor. Wort für Wort.",
  "Ein Stuhl quietscht bei jeder Bewegung.",
  "Jemand fragt: 'Können wir das nochmal von vorne erklären?'",
  "Das Licht geht aus. Bewegungsmelder. Jemand muss winken.",
  "Ein Kollege hat den Bildschirm gespiegelt. Man sieht seine Slack-DMs.",
  "'Next Steps?' — Totenstille.",
  "Jemand zählt laut die Teilnehmer. 'Fehlt noch jemand?'",
  "Ein Kollege tippt so laut, dass alle denken, es hagelt.",
  "Die Uhr tickt hörbar. Jede Sekunde fühlt sich wie eine Minute an.",
  "Jemand wispert ins Telefon. Alle tun so, als würden sie es nicht hören.",
  "Der Marketing-Guru sagt 'Low-Hanging Fruit'. Alle nehmen einen Schluck.",
  "Ein Teilnehmer ist eingefroren. Sein Gesicht bleibt 2 Minuten stehen.",
  "Jemand stellt eine Frage, die vor 5 Minuten schon beantwortet wurde.",
  // Neue Flavor-Texte
  "Ein Post-It fällt vom Whiteboard. Niemand hebt es auf.",
  "Jemand gähnt ansteckend. Drei weitere folgen.",
  "Der Praktikant notiert alles. ALLES. Sogar die Pausen.",
  "'Ich glaube, wir drehen uns im Kreis.' — 'Nein, das war ein Quadrat.'",
  "Ein Kollege hat seine Kinder im Hintergrund. Sie spielen Schlagzeug.",
  "Jemand fragt nach dem WLAN-Passwort. Zum dritten Mal diese Woche.",
  "Der Scrum Master zeichnet ein Burndown-Chart auf Papier. Es geht nur nach oben.",
  "Ein Rauchmelder piept leise im Hintergrund. Alle ignorieren es.",
  "Jemand trinkt aus einer Tasse mit der Aufschrift 'Ich hasse Montage'. Es ist Donnerstag.",
  "'Wir brauchen mehr Alignment.' — Niemand weiß, worüber.",
  "Ein Kollege hat seine Kamera an, aber starrt offensichtlich auf ein zweites Display.",
  "Jemand sagt 'Let's circle back'. Ein innerer Schrei hallt durch den Raum.",
  "Der Abteilungsleiter zeigt ein Tortendiagramm. Alle Segmente heißen 'Sonstiges'.",
  "Ein Kollege antwortet auf eine Frage, die vor 20 Minuten gestellt wurde.",
  "Jemand hat vergessen, seinen virtuellen Hintergrund auszuschalten. Er sitzt 'am Strand'.",
  "Der Chef sagt 'Danke für eure Zeit'. Es klingt wie eine Drohung.",
  "'Kurze Frage...' — Die Frage dauert 7 Minuten.",
  "Ein Lieferdienst klingelt bei einem Home-Office-Teilnehmer. Laut. Mehrfach.",
  "Jemand rückt seinen Stuhl hin und her. Das Geräusch geht durch Mark und Bein.",
  "Der Designer öffnet Figma. Der Laptop friert ein.",
  "'Dazu habe ich eine Folie...' — Es sind zwölf Folien.",
  "Ein Kollege murmelt 'Das hätte eine E-Mail sein können.' — Alle nicken innerlich.",
  "Jemand hat seinen Bildschirmschoner nicht deaktiviert. Fliegende Toaster.",
  "Der Product Owner ändert spontan die Prioritäten. Zum vierten Mal heute.",
  "Ein Kollege isst Möhren. Es klingt wie ein Biber bei der Arbeit.",
  "'Wer hat noch Fragen?' — Alle schauen auf den Tisch.",
  "Ein Fenster geht nicht zu. Oder auf. Niemand weiß es genau.",
  "Jemand sagt 'Let's take this offline' und meint damit 'Halt den Mund'.",
  "Der Teamleiter macht sich Notizen auf seinem Tablet. Es ist Sudoku.",
  "Ein Kollege lacht plötzlich. Er hat eine Slack-Nachricht bekommen.",
  "Jemand atmet so laut ins Mikro, dass es klingt wie ein Sturm.",
  "'Ich teile mal meinen Bildschirm.' — 47 Tabs. Einer davon ist Zalando.",
  "Der HR-Manager erwähnt 'Wellbeing'. Alle werden müder.",
  "Ein Stuhl bricht zusammen. Der Kollege tut so, als wäre nichts passiert.",
  "Jemand summt leise vor sich hin. Es ist der Ohrwurm von gestern.",
  "Der Chef fragt: 'Hat jemand noch einen Punkt?' — Bitte. Nein. Bitte nicht.",
  "'Das besprechen wir im nächsten Meeting.' — Es gibt immer ein nächstes Meeting.",
  "Ein Kollege fotografiert das Whiteboard. Mit Blitz. Alle blinzeln.",
  "Jemand hat 14 Browser-Tabs offen. Alle machen Geräusche.",
  "Der Kaffee im Automaten kostet jetzt 50 Cent mehr. Empörung.",
  "Ein Kollege sagt 'Proaktiv'. Das Buzzword-Bingo im Kopf ist fast voll.",
  "Jemand niest so laut, dass alle zusammenzucken.",
  "'Schnelle Umfrage...' — Dauert länger als das eigentliche Thema.",
  "Der Praktikant fragt, ob man das Meeting auch aufnehmen kann. Panik.",
  "Ein Kollege hat seine AirPods verloren. Die ganze Abteilung sucht mit.",
  "Jemand stellt seinen Kaffee ab. Auf dem Laptop des Nachbarn.",
  "Der Scrum Master sagt 'Timeboxed'. Das Meeting ist schon 20 Minuten über der Zeit.",
  "'Wie seht ihr das?' — Stille. Blicke auf Handys.",
  "Ein Kollege zieht sich die Schuhe aus. Niemand sagt etwas. Alle leiden.",
  "Jemand hat die Agenda vergessen. Es gab eine?",
  "Der Beamer projiziert auf die falsche Wand. Der Vortrag geht trotzdem weiter.",
  "Ein Kollege erzählt von seinem Wochenende. Ausführlich. Niemand hat gefragt.",
  "Jemand verschüttet seinen Kaffee. Stühle rücken. Papiertücher fliegen.",
  // Kürzere Interjektionen
  "Irgendwo klingelt ein Handy. Niemand geht ran.",
  "Stille. Awkward.",
  "Ein kollektives Seufzen geht durch den Raum.",
  "Jemand unterdrückt ein Gähnen. Erfolglos.",
  "Der Beamer flackert kurz. Alle hoffen.",
  "Ein leises 'Wann ist das hier vorbei?' ist zu hören.",
  "Jemand klickt seinen Kugelschreiber. Klick. Klick.",
  "Ein Magen knurrt laut. Alle tun so, als hätten sie es nicht gehört.",
  "Die Klimaanlage macht ein seltsames Geräusch.",
  "Ein Kollege schaut demonstrativ auf die Uhr.",
  "Jemand wispert: 'Ich kann nicht mehr.'",
  "Ein Husten. Dann noch einer. Dominoeffekt.",
  "Der Kaffeeduft aus dem Flur ist ablenkend gut.",
  "Ein Stuhl quietscht erbarmungslos.",
  "Jemand schreibt hektisch eine Nachricht unter dem Tisch.",
  "Ein Telefon vibriert. Es hört nicht auf.",
  "Draußen lacht jemand laut. Neid macht sich breit.",
  "Der Drucker im Nebenraum springt an. Ratatatata.",
  "Ein Kollege nickt intensiv. Schläft er?",
  "Jemand räuspert sich. Dreimal. Laut.",
  "Die Tür geht auf. Falscher Raum. Tür geht zu.",
  "Ein leises Schnarchen? Nein, die Heizung. Wahrscheinlich.",
  "Ein Post-It segelt sanft vom Whiteboard auf den Boden.",
  "Jemand murmelt 'Agenda-Punkt 47...' — Gibt es nur 5.",
  "Der Kaffeeautomat im Flur gurgelt bedrohlich.",
  "Ein Handy-Alarm geht los. 'TRINKEN!' steht auf dem Display.",
  "Ein Kollege starrt intensiv seinen leeren Notizblock an.",
  "Die Heizung macht Klopfgeräusche. Morse-Code?",
  "Ein dezenter Geruch von Mikrowellen-Essen zieht herein.",
  "Jemand sortiert lautstark Papiere. Jetzt. Wirklich jetzt.",
  // Die neue Arbeitsweise
  "Der KI-Notetaker ist dem Call beigetreten. Er ist jetzt der aufmerksamste Teilnehmer.",
  "Der Meeting-Bot protokolliert mit. Er hat drei Dinge notiert, die niemand gesagt hat.",
  "'Ich hab das mal kurz von der KI vorbereiten lassen.' — Es sind 14 Folien.",
  "Jemand liest sichtbar eine KI-Antwort vom Bildschirm ab. Inklusive 'Gerne!'.",
  "Die KI-Zusammenfassung des letzten Meetings ist länger als das Meeting.",
  "'Wir müssen da AI-first denken.' Niemand fragt nach, was das heißt.",
  "'Das ist zu 100% KI-generiert, aber ich hab drübergeschaut.' — Hat er nicht.",
  "Die KI schlägt als nächsten Schritt ein Follow-Up-Meeting vor. Natürlich.",
  "Jemand hat ein KI-Avatar-Profilbild. Es hat elf Finger und ein sehr gutes Selbstbewusstsein.",
  "'Können wir da nicht einen Agenten drauf ansetzen?' — Auf ein zweiseitiges Protokoll.",
  "Das Transkript nennt dich durchgehend 'Martin'. Du heißt nicht Martin.",
  "Der Prompt wird live im Meeting optimiert. Runde vier.",
  "'Die KI hat das validiert.' — Die KI hat es geschrieben.",
  "Marketing zeigt eine KI-generierte Roadmap. Q5 sieht vielversprechend aus.",
  "Jemand fragt, ob die KI das Meeting hätte übernehmen können. Betretenes Schweigen.",
  "Das Rechenzentrum-Kontingent ist aufgebraucht. Alle müssen heute selbst denken.",
  "Der Assistent hat die Agenda geschrieben. Punkt 1: 'Agenda besprechen'.",
  "'Ich hab das mit der KI gegengecheckt.' — Die KI hat es vorgeschlagen.",
  "Jemand fragt, ob das Transkript DSGVO-konform ist. Langes Schweigen.",
  "Der Bot fasst zusammen: 'Es wurde diskutiert.' Das trifft es.",
  "'Die KI sagt, wir liegen im Plan.' Der Plan ist auch von der KI.",
  "Zwei Leute lesen sich gegenseitig KI-Antworten vor. Niemand greift ein.",
  "Der Assistent schlägt 'weitere Abstimmung' vor. Zum vierten Mal.",
  "Die Präsentation hat drei Tippfehler. Alle drei sind generiert.",
  "'Wir haben da einen Agenten gebaut.' — Es ist ein Cronjob.",
  "Der Notetaker hat sich selbst als Teilnehmer eingetragen.",
  "Jemand bedankt sich beim Chatbot. Aus Gewohnheit.",
  "'Kann die KI das nicht bis morgen?' Die KI hat schon zugesagt.",
];

// ---- Zufallsbegegnungen zwischen den Meetings ----

export const RANDOM_ENCOUNTERS: RandomEncounter[] = [
  {
    id: "putzfrau",
    name: "Putzfrau Olga",
    emoji: "🧹",
    intro: "Die Putzfrau Olga blockiert den Flur mit ihrem Wagen. Sie schaut dich vielsagend an.",
    choices: [
      {
        label: "💬 Smalltalk halten",
        message: "Olga freut sich über das Gespräch und gibt dir einen Energy-Drink aus ihrem Wagen.",
        effects: { fatigue: -20 },
      },
      {
        label: "🙋 Höflich grüßen & vorbei",
        message: "Olga nickt zufrieden. 'Du bist nett. Die anderen ignorieren mich immer.'",
        effects: { addRandomPositivePerk: true },
        alternateMessage: "Olga schaut enttäuscht. 'Immer alle in Eile...' Sie fährt dir über den Fuß.",
        alternateEffects: { fatigue: 5 },
        alternateChance: 0.25,
      },
    ],
  },
  {
    id: "hausmeister",
    name: "Hausmeister Werner",
    emoji: "🔧",
    intro: "Hausmeister Werner steht mit einer Leiter im Flur. 'Hey, kannst du mal kurz halten?'",
    choices: [
      {
        label: "🧑‍🔧 Leiter halten",
        message: "Werner bedankt sich herzlich. 'Hier, nimm das als Dankeschön.' Er gibt dir einen Schokoriegel.",
        effects: { fatigue: -10, salary: 25 },
      },
      {
        label: "⏳ Keine Zeit, sorry!",
        message: "Werner murmelt etwas über 'die Jugend von heute'. Später ist die Heizung in deinem Büro aus.",
        effects: { addRandomNegativePerk: true },
        alternateMessage: "Werner zuckt die Schultern. Nichts passiert.",
        alternateEffects: {},
        alternateChance: 0.4,
      },
    ],
  },
  {
    id: "postbote",
    name: "Postbote Rainer",
    emoji: "📦",
    intro: "Postbote Rainer steht mit einem mysteriösen Paket vor dir. 'Für Sie! ...glaube ich.'",
    choices: [
      {
        label: "📦 Paket annehmen",
        message: "Das Paket enthält einen ergonomischen Mauskissen-Handgelenkaufleger. Du fühlst dich ausgeruht!",
        effects: { fatigue: -25 },
        alternateMessage: "Das Paket enthält 200 Visitenkarten mit falschem Namen. Nutzlos.",
        alternateEffects: { fatigue: 5 },
        alternateChance: 0.3,
      },
      {
        label: "❌ Ablehnen",
        message: "Rainer zuckt die Schultern und geht weiter. Nichts passiert.",
        effects: {},
      },
    ],
  },
  {
    id: "kantine",
    name: "Kantinenchefin Helga",
    emoji: "🍲",
    intro: "Kantinenchefin Helga hat Reste übrig. 'Willst du was? Gibt Gulasch oder Salat.'",
    choices: [
      {
        label: "🍛 Gulasch nehmen",
        message: "Das Gulasch ist überraschend gut! Du fühlst dich gestärkt.",
        effects: { fatigue: -15, salary: 0 },
        alternateMessage: "Das Gulasch war von gestern. Dir ist übel.",
        alternateEffects: { addRandomNegativePerk: true },
        alternateChance: 0.2,
      },
      {
        label: "🥗 Salat nehmen",
        message: "Gesunde Ernährung! Du fühlst dich leicht und fokussiert.",
        effects: { fatigue: -8, addRandomPositivePerk: true },
      },
    ],
  },
  {
    id: "praktikant",
    name: "Der neue Praktikant",
    emoji: "👶",
    intro: "Ein neuer Praktikant steht verloren im Flur. 'Entschuldigung, wo ist der Konferenzraum B?'",
    choices: [
      {
        label: "📍 Weg zeigen",
        message: "Der Praktikant ist dankbar. 'Danke! Hier, ich hab noch Gummibärchen!' Später erzählt er dem Chef, wie nett du warst.",
        effects: { salary: 30, fatigue: -5 },
      },
      {
        label: "🤷 Falschen Weg zeigen",
        message: "Du schickst ihn in den Serverraum. Lustig, aber es kommt raus.",
        effects: { salary: -20 },
        alternateMessage: "Du schickst ihn in den Serverraum. Niemand merkt es. Slapstick-Unterhaltung!",
        alternateEffects: { fatigue: -15 },
        alternateChance: 0.5,
      },
    ],
  },
  {
    id: "kaffeemaschine",
    name: "Defekte Kaffeemaschine",
    emoji: "☕",
    intro: "Die Kaffeemaschine im Flur macht komische Geräusche und blinkt rot.",
    choices: [
      {
        label: "🔧 Reparieren versuchen",
        message: "Nach kurzer Fummelei läuft sie wieder! Du belohnst dich mit einem Kaffee.",
        effects: { fatigue: -30 },
        alternateMessage: "Du machst es schlimmer. Kaffee spritzt überall hin. Peinlich.",
        alternateEffects: { fatigue: 10, salary: -15 },
        alternateChance: 0.35,
      },
      {
        label: "🚶 Ignorieren",
        message: "Du gehst weiter. Die Maschine explodiert später. Nicht dein Problem.",
        effects: {},
      },
    ],
  },
  {
    id: "coach",
    name: "Agile Coach Sven",
    emoji: "🧘",
    intro: "Agile Coach Sven fängt dich auf dem Flur ab. 'Hast du 2 Minuten für eine Retro?'",
    choices: [
      {
        label: "🗣️ Mitmachen",
        message: "Sven gibt dir erstaunlich gute Tipps. Du fühlst dich fokussiert.",
        effects: { removeNegativePerk: true, fatigue: -10 },
      },
      {
        label: "🏃 Flucht ergreifen",
        message: "Du entkommst Svens Retro-Falle. Aber er merkt sich das...",
        effects: {},
        alternateMessage: "Du rennst weg, aber Sven ist schneller. Zwangs-Retro! Es war aber gar nicht so schlimm.",
        alternateEffects: { fatigue: -5 },
        alternateChance: 0.3,
      },
    ],
  },
  {
    id: "lieferant",
    name: "Lieferdienst-Fahrer",
    emoji: "🛵",
    intro: "Ein Lieferdienst-Fahrer steht mit Bürobedarf im Eingang. 'Können Sie unterschreiben?'",
    choices: [
      {
        label: "✍️ Unterschreiben",
        message: "Du unterschreibst und erhältst dafür einen Kugelschreiber als Dank. Nett.",
        effects: { salary: 10 },
        alternateMessage: "Du hast versehentlich für 500 Toner-Kartuschen unterschrieben. HR ist sauer.",
        alternateEffects: { salary: -50 },
        alternateChance: 0.15,
      },
      {
        label: "❌ Weitergehen",
        message: "Nicht dein Job. Du gehst weiter.",
        effects: {},
      },
    ],
  },
  {
    id: "aufzug",
    name: "Aufzug steckt fest",
    emoji: "🚷",
    intro: "Du steigst in den Aufzug und er bleibt zwischen zwei Stockwerken stecken!",
    choices: [
      {
        label: "📲 IT anrufen",
        message: "Die IT braucht 10 Minuten. Aber du nutzt die Pause für ein Power-Nap.",
        effects: { fatigue: -35 },
      },
      {
        label: "🧗 Klettern",
        message: "Du kletterst durch die Dachluke! Action-Held-Vibes. Leider hat es die Security-Kamera gesehen.",
        effects: { salary: -25, fatigue: -10 },
        alternateMessage: "Du kletterst heroisch durch die Dachluke. Der Chef ist beeindruckt!",
        alternateEffects: { salary: 40, fatigue: -20 },
        alternateChance: 0.4,
      },
    ],
  },
  {
    id: "feueralarm",
    name: "Fehlalarm",
    emoji: "🚨",
    intro: "Der Feueralarm geht los! Alle rennen nach draußen.",
    choices: [
      {
        label: "🚶 Geordnet rausgehen",
        message: "An der frischen Luft sammelst du neue Energie. Der Fehlalarm war ein Segen.",
        effects: { fatigue: -20, addRandomPositivePerk: true },
      },
      {
        label: "🎰 Am Schreibtisch bleiben",
        message: "Alle sind draußen, du hast 15 Minuten Ruhe! Perfekt zum Prokrastinieren.",
        effects: { fatigue: -40 },
        alternateMessage: "Der Brandschutzbeauftragte findet dich. Strafzettel.",
        alternateEffects: { salary: -35 },
        alternateChance: 0.25,
      },
    ],
  },
  {
    id: "serverraum",
    name: "Serverraum-Zugang",
    emoji: "🖥️",
    intro: "Die Tür zum Serverraum steht offen. Es ist kühl und leise drin.",
    choices: [
      {
        label: "❄️ Reingehen & abkühlen",
        message: "Die Kälte erfrischt dich. Du findest außerdem einen USB-Stick mit nützlichen Tools.",
        effects: { fatigue: -25, dropRandomPowerup: true },
      },
      {
        label: "🚪 Tür schließen & weitergehen",
        message: "Verantwortungsvoll! Der Facility Manager bedankt sich später per E-Mail.",
        effects: { salary: 20 },
      },
    ],
  },
  {
    id: "pizzabote",
    name: "Pizzabote im Foyer",
    emoji: "🍕",
    intro: "Ein Pizzabote steht ratlos im Foyer. 'Kennen Sie einen Max Müller? Niemand macht auf.'",
    choices: [
      {
        label: "🍕 Pizza selbst nehmen",
        message: "Du nimmst die Pizza. Max Müller existiert hier gar nicht. Score!",
        effects: { fatigue: -30, dropRandomPowerup: true },
        alternateMessage: "Du nimmst die Pizza — aber es ist Ananas mit Sardellen. Ekelhaft.",
        alternateEffects: { fatigue: -5 },
        alternateChance: 0.2,
      },
      {
        label: "📱 Max Müller suchen",
        message: "Du suchst 10 Minuten. Es gibt keinen Max Müller. Der Pizzabote gibt dir die Pizza als Dank.",
        effects: { fatigue: -15 },
      },
    ],
  },
  {
    id: "ceoImAufzug",
    name: "CEO im Aufzug",
    emoji: "👔",
    intro: "Du stehst allein mit dem CEO im Aufzug. Awkward silence.",
    choices: [
      {
        label: "💬 Elevator Pitch halten",
        message: "Der CEO ist beeindruckt! 'Gute Idee, schreib mir eine Mail.' Gehaltsbonus!",
        effects: { salary: 100 },
        alternateMessage: "Der CEO hört nicht zu. Er scrollt auf seinem Handy. Peinliche Stille.",
        alternateEffects: {},
        alternateChance: 0.35,
      },
      {
        label: "🤐 Schweigen",
        message: "Der CEO nickt dir zu und steigt aus. Nichts passiert. Oder doch? Er merkt sich Gesichter...",
        effects: { addRandomPositivePerk: true },
        alternateMessage: "Der CEO murmelt 'Wer sind Sie?' Nicht ideal.",
        alternateEffects: { addRandomNegativePerk: true },
        alternateChance: 0.25,
      },
    ],
  },
  {
    id: "kabelchaos",
    name: "Kabelchaos",
    emoji: "🔌",
    intro: "Ein Kabelbündel hängt aus der Decke. Jemand hat 'NICHT ANFASSEN' draufgeschrieben.",
    choices: [
      {
        label: "🔌 Kabel sortieren",
        message: "Du sortierst die Kabel wie ein Profi. Das Netzwerk läuft wieder! Dankbare Kollegen!",
        effects: { salary: 40, fatigue: -10, addRandomPositivePerk: true },
        alternateMessage: "Du ziehst am falschen Kabel. Das WLAN im ganzen Stockwerk fällt aus.",
        alternateEffects: { salary: -30, addRandomNegativePerk: true },
        alternateChance: 0.3,
      },
      {
        label: "📸 Foto machen & IT melden",
        message: "Die IT bedankt sich. 'Endlich meldet das mal jemand.' Kleiner Bonus.",
        effects: { salary: 15 },
      },
    ],
  },
  {
    id: "kaffeeAutomat",
    name: "Kaffeeautomat-Jackpot",
    emoji: "☕",
    intro: "Der Kaffeeautomat spuckt endlos Kaffee aus! Eine Schlange bildet sich.",
    choices: [
      {
        label: "☕ Gratis-Kaffee abgreifen",
        message: "Drei Tassen gratis! Du bist hellwach und hast sogar einen für den Teamleiter mitgebracht.",
        effects: { fatigue: -35, dropRandomPowerup: true },
      },
      {
        label: "🛠️ Techniker rufen",
        message: "Du rufst den Techniker. Er repariert es und gibt dir einen Kaffee als Dank.",
        effects: { fatigue: -15, salary: 10 },
      },
    ],
  },
  {
    id: "verloreneUnterlagen",
    name: "Verlorene Unterlagen",
    emoji: "📂",
    intro: "Du findest vertrauliche Unterlagen auf dem Kopierer. 'Gehaltstabelle Q3'...",
    choices: [
      {
        label: "👀 Kurz reinschauen",
        message: "Du siehst, dass der Praktikant mehr verdient als du. Motivation sinkt. Aber du weißt jetzt Bescheid.",
        effects: { addRandomNegativePerk: true, salary: 50 },
      },
      {
        label: "📤 Ungelesen zurückgeben",
        message: "Die HR-Chefin ist dankbar. 'Sehr integer von Ihnen!' Bonus-Bewertung.",
        effects: { salary: 60, addRandomPositivePerk: true },
      },
    ],
  },
  {
    id: "spontaneMassage",
    name: "Spontane Massage",
    emoji: "💆",
    intro: "Ein mobiler Massage-Service steht im Foyer. 'Gratis-Probemassage für Mitarbeiter!'",
    choices: [
      {
        label: "💆 Nacken-Massage annehmen",
        message: "5 Minuten pure Entspannung. Du fühlst dich wie neugeboren!",
        effects: { fatigue: -40, removeNegativePerk: true },
      },
      {
        label: "⏰ Keine Zeit, danke",
        message: "Du bist zu gestresst für eine Anti-Stress-Massage. Die Ironie ist dir bewusst.",
        effects: {},
      },
    ],
  },
  {
    id: "mysterioeserKoffer",
    name: "Mysteriöser Koffer",
    emoji: "💼",
    intro: "Ein herrenloser Koffer steht neben deinem Schreibtisch. Er hat einen Zettel: 'Für den Fleißigsten'.",
    choices: [
      {
        label: "💼 Koffer öffnen",
        message: "Drin ist ein Survival-Kit: Energy-Drink, Kopfhörer und Notfall-Snacks!",
        effects: { fatigue: -20, dropRandomPowerup: true },
        alternateMessage: "Drin sind nur alte Firmen-T-Shirts in XXL. Immerhin warm.",
        alternateEffects: { fatigue: -5 },
        alternateChance: 0.25,
      },
      {
        label: "🚨 Security rufen",
        message: "Security gibt Entwarnung. Es war ein Werbegeschenk. Du bekommst ein Dankeschön.",
        effects: { salary: 25 },
      },
    ],
  },
  {
    id: "heimlicherRaum",
    name: "Geheimer Pausenraum",
    emoji: "🚪",
    intro: "Du entdeckst einen versteckten Pausenraum hinter dem Archiv. Sofa, Snacks, Stille.",
    choices: [
      {
        label: "🛋️ Power-Nap machen",
        message: "20 Minuten auf dem Sofa. Die beste Entscheidung des Tages!",
        effects: { fatigue: -45 },
        alternateMessage: "Du schläfst zu lang ein! Der Abteilungsleiter findet dich. Unangenehm.",
        alternateEffects: { fatigue: -20, salary: -40 },
        alternateChance: 0.2,
      },
      {
        label: "🍫 Snacks mitnehmen",
        message: "Du packst dir die Taschen voll. Schokoriegel für den Rest des Tages!",
        effects: { fatigue: -15, dropRandomPowerup: true },
      },
    ],
  },
  {
    id: "buerohund",
    name: "Bürohund Max",
    emoji: "🐕",
    intro: "Ein Golden Retriever läuft durch den Flur! Jemand hat seinen Hund mitgebracht.",
    choices: [
      {
        label: "🐕 Hund streicheln",
        message: "Max wedelt begeistert. Dein Cortisol-Level sinkt sofort. Beste Therapie!",
        effects: { fatigue: -30, removeNegativePerk: true },
      },
      {
        label: "📷 Foto fürs Team-Chat",
        message: "Das Foto geht viral im Firmen-Slack. Du bist der Held des Tages!",
        effects: { addRandomPositivePerk: true, salary: 15 },
      },
    ],
  },
  {
    id: "kiBeauftragter",
    name: "KI-Beauftragter Marcel",
    emoji: "🤖",
    intro: "Marcel wurde letzte Woche zum 'AI Transformation Lead' ernannt. Was das heißt, weiß er noch nicht genau — aber er hat schon Folien.",
    choices: [
      {
        label: "🎞️ Dir die Folien ansehen",
        message: "Marcel zeigt dir 40 Folien in 5 Minuten. Danach gibt er dir einen Kaffee aus seinem Innovationsbudget.",
        effects: { fatigue: -15, salary: 20 },
      },
      {
        label: "💡 Ihm einen echten Use Case nennen",
        message: "Marcel notiert deinen Vorschlag und nennt dich fortan seinen Sparringspartner. Das öffnet Türen.",
        effects: { addRandomPositivePerk: true },
        alternateMessage: "Marcel trägt dich als Verantwortlichen für den Use Case ein. Herzlichen Glückwunsch.",
        alternateEffects: { addRandomNegativePerk: true },
        alternateChance: 0.35,
      },
      {
        label: "🚪 'Ich hab gleich ein Meeting'",
        message: "Marcel nickt verständnisvoll. Er hat auch gleich eins. Alle haben gleich eins.",
        effects: {},
      },
    ],
  },
];

// ---- Richtungswechsel: was jemand sagt, wenn die Runde kippt ----

export const DIRECTION_CHANGE_QUOTES = [
  "Moment, fangen wir nochmal von vorne an!",
  "Ups, andersrum!",
  "Wir drehen die Runde um!",
  "Können wir die Reihenfolge ändern?",
  "Ich finde, wir sollten andersrum durchgehen.",
  "Der Notetaker hat die Reihenfolge geändert. Niemand widerspricht einem Bot.",
  "'Die KI schlägt vor, andersherum durchzugehen.'",
];

// ---- Angesprochen werden ----

export function addressPhrases(
  { playerName, playerRole, hint }: AddressPhraseContext,
): Record<AddressMethod, string[]> {
  return {
    name: [
      `"${hint(playerName)}, was sagst du dazu?"`,
      `"${hint(playerName)}, deine Meinung?"`,
      `"Ach ${hint(playerName)}, du hattest doch letztes Mal..."`,
      `"Hey ${hint(playerName)}, kurze Frage..."`,
      `"Was hältst du davon, ${hint(playerName)}?"`,
    ],
    role: [
      `"Unser ${hint(playerRole)} kann das sicher einschätzen."`,
      `"Na, als ${hint(playerRole)} weißt du das ja..."`,
      `"Das ist doch was für unseren ${hint(playerRole)}!"`,
      `"Vielleicht kann unser ${hint(playerRole)} was dazu sagen?"`,
    ],
    informal: [
      `"${hint("Du")} da hinten, was meinst du?"`,
      `"Und ${hint("du")}? Alles klar soweit?"`,
      `"${hint("Du")} bist doch zuständig, oder?"`,
      `"Hey, ${hint("du")} — kurz deine Einschätzung?"`,
    ],
  };
}
